/**
 * Action dispatcher — `fireAction(step)`.
 *
 * Multi-bridge Phase A. Replaces the legacy `bridge.sendMidi(...)` /
 * `bridge.armSequencer(...)` / etc. calls one step at a time. Reads
 * `step.target` → resolves to a `BridgeConnection` → calls the matching
 * OPBridge HTTP endpoint via the connection's `httpClient()`.
 *
 * Scope this commit: MIDI-CC, MIDI-note, MIDI-raw. Engine pushes
 * (sequencer / macro library, LFO arm) follow in a later phase once the
 * OPButtons → OPBridge schema translation is mapped out — those payload
 * shapes don't match 1:1 today.
 *
 * Target resolution rules (opbridge/store.ts's resolveBridges/resolveBridge —
 * shared with per-item liveness so the two can never disagree):
 *  - `step.target` (an item's or its pedal's `bridgeId`) matches a
 *    `BridgeConfig.id` from the local store → that bridge. No match → empty
 *    result, surfaced as `no-bridges`/`bridge-unknown` — never silently
 *    substitutes a different bridge (DEC-138).
 *  - the literal value `"all"` → fan out to every bonded bridge; each
 *    per-bridge dispatch call below checks its own connection status, so an
 *    offline bonded bridge reports its own failure rather than being
 *    silently skipped.
 *  - empty / undefined → the default bridge (`BridgeConfig.is_primary` —
 *    what a new item inherits until the operator assigns it elsewhere via
 *    the Inspector/Connected Devices bridge picker).
 *
 * MIDI port resolution: each `BridgeConfig` carries a `defaultMidiOutput`
 * (set via the BridgesPanel row). Until per-action port routing lands on
 * the schema, every MIDI step on that bridge goes to that port. Returns
 * a `port-not-configured` error if absent — caller decides whether to
 * surface a toast or stay silent.
 */

import type {
  ActionStep,
  AbletonTempoStep,
  AbletonTimeSigStep,
  AbletonTransportStep,
  AbletonRecQuantStep,
  AbletonLaunchQuantStep,
  AbletonMetronomeStep,
  AbletonResyncStep,
  AbletonSustainKillStep,
  MidiCcStep,
  MidiNoteStep,
  MidiProgramChangeStep,
  MidiRawStep,
  MidiStepBase,
  ObsSceneStep,
  AbletonDeviceStep,
  ProgramLaunchStep,
  LightingFireStep,
  LightingStopStep,
  Sequencer,
  Macro,
  SequencerMidiTarget,
  MacroMidiTarget,
  LaneDmxTarget,
} from '@opbuttons/schema';
import { normalizeRecQuant, normalizeLaunchQuant, isDmxLaneTarget, type RecQuantLabel, type RecQuantVia } from '@opbuttons/schema';
import { bridges, getConnection, resolveBridges, isCaptureBridge, anyBridgeConnectedReactive, bridgeConnectedReactive } from './store.ts';
import { fixtures } from '../fixtures/store.ts';
import type { BridgeConfig } from './storage.ts';
import { OpbridgeError, machineNameFromInstanceName, type RuleAction, type ToggleState } from './httpClient.ts';
import { canonicalJson } from '../timeline/librarySync.ts';
import type { BridgeConnection } from './connection.ts';
import { writeSendCacheRaw, midiCcTargetId, midiNoteTargetId, DEFAULT_PORT_TOKEN } from '../liveState/sendCache.ts';
import { abletonState, liveClockReport } from './clockPoll.ts';
import { crossfadeSend, landSends, liveBeatsPerBar, MOTION_CAPTURE_ADDRESS, MOTION_OSC_TARGET } from '../timeline/opMotion.ts';
import { startMotionCapture } from '../timeline/motionCapture.ts';
import { motionPresetById } from '../layout/projectStore.ts';
import { recordObsFailure, recordObsSceneSwitch } from '../obs/scenes.ts';
import { clearAllPresetLayers } from '../lighting/presets.ts';
import {
  recordProgramRunning, recordProgramsFailure, recordProgramPending, clearProgramPending,
} from '../program/programs.ts';
import { BUTTONS_DEVICE_TARGET, SET_DEVICE_ON_ADDRESS } from '../ableton/devicePoll.ts';
import { DEFAULT_OPV_VIA, OPVISUAL_OSC_TARGET, panicMsg, type OpvMsg, type OpvVia } from '../opvisual/wire.ts';
import {
  setPracticeBpm,
  setPracticeSig,
  setPracticePlaying,
} from '../clock/practiceClock.ts';

// ============================================================
// Public API
// ============================================================

/** What went wrong, in words a performer reads (2026-10-16: messages printed the raw kind, "Jump failed: no-bridges",
 *  and called the same machine a computer, a bridge and "your main bridge"). Always "computer", named when known; an
 *  OPBridge reply's own message is kept as it said it. */
export function plainFireReason(r: { kind: string; target?: string; detail?: string }): string {
  const name = r.target ? bridges().find((b) => b.id === r.target)?.name : undefined;
  switch (r.kind) {
    case 'no-bridges': return 'no computer is connected';
    case 'bridge-unknown': return 'that computer is not paired with this device any more';
    case 'bridge-disconnected': return `${name ?? 'the computer'} is not reachable right now`;
    case 'port-not-configured': return `no MIDI port is chosen for it${name ? ` on ${name}` : ''}`;
    case 'bad-note': return 'its note is out of range';
    case 'unsupported-step': return r.detail ?? 'it has nothing to send';
    case 'app-not-linked': return r.detail ?? 'that app is not connected to OPBridge';
    case 'http-error': return r.detail ? `${name ?? 'OPBridge'} said: ${r.detail}` : `${name ?? 'OPBridge'} refused it`;
    default: return r.detail ?? r.kind;
  }
}

export type FireResultKind =
  | 'ok'
  | 'no-bridges'
  | 'bridge-unknown'
  | 'bridge-disconnected'
  | 'port-not-configured'
  | 'bad-note'
  | 'unsupported-step'
  | 'http-error'
  /** The bridge took the request, but the APP it is for is not connected to
   *  that bridge, so nothing happened. Today only OPVisual's
   *  `/opvisual/trigger` can say this (its `opvisual_connected: false`).
   *  Distinct from `bridge-disconnected`, which is about the bridge itself:
   *  the fix is in the other app, not in OPButtons or OPBridge. */
  | 'app-not-linked';

export interface FireResult {
  /** The bridge id this attempt was routed to (or `"all"` if fan-out). */
  target: string;
  kind: FireResultKind;
  /** Free-form context — usually OPBridge's error body, the missing port
   *  name, or "step type X not yet wired". */
  detail?: string;
  /** Present on an `'ok'` result when the attempt succeeded overall but
   *  silently dropped some content the server can't handle yet (e.g. a
   *  Timeline push where Stream Deck steps resolve to zero cues — see
   *  `timeline/bridgeSync.ts`). Never set on a failure result; a total
   *  drop already surfaces via `'unsupported-step'` instead. 2026-08-21. */
  partialDropWarning?: string;
  /** A light preset press (OPBridge S297): its lights that someone else holds, so they show nothing. */
  parked?: Array<{ light: string; by: string }>;
  /** A Song timeline's start: whether its linked test track is playing with it, or why not (OPBridge S318,
   *  `timeline_test_track`). Absent when the timeline has no song or the bridge does not bring songs. */
  song?: { playing: boolean; output?: string; reason?: string; message?: string };
  /** A timeline start that made the timeline lead the clock, Live being gone (OPBridge S342): its tempo. */
  leads?: { bpm: number };
  /** A timeline push sent with `?live=true` (OPBridge S306): how many
   *  RUNNING copies took the new version (0 = it was not playing there).
   *  Absent when the push was not live, including on a bridge too old to
   *  update a running timeline. */
  liveCopies?: number;
  /** A timeline push OPBridge refused because it holds a NEWER copy (409 `newer_copy`, `timeline_put_newer_guard`,
   *  OPBridge S324): its `modified`. Nothing was replaced, and that newer copy is what plays. */
  newerCopy?: string;
}

/** Resolve an item's `bridgeIds` (multi-select bridge affinity,
 *  2026-08-13) into the literal single-bridge target strings to dispatch
 *  against. `undefined` (not yet configured) becomes exactly one `''`
 *  target — the dynamic default bridge, same as every dispatch function's
 *  existing empty-target fallback. An explicit list (possibly empty) is
 *  used as-is: `[]` means the operator deliberately selected zero
 *  destinations, so the caller's `Promise.all` loop below runs zero times
 *  and produces zero results — callers must treat that as "not
 *  configured," never as a send failure.
 *
 *  Every dispatch function in this file (`fireAction`, `armLfo`, etc.)
 *  already resolves ONE target per call via `resolveTargets`/
 *  `resolveBridges`, so fan-out to N bridges is N calls from the caller,
 *  not a wire-level fan-out here — this helper only turns the item's
 *  config into that call list. Typical caller shape:
 *
 *    const results = (await Promise.all(
 *      fanoutTargets(item.bridgeIds).map((target) => fireAction({ ...step, target }))
 *    )).flat();
 */
export function fanoutTargets(bridgeIds: string[] | undefined): string[] {
  return bridgeIds === undefined ? [''] : bridgeIds;
}

/**
 * Fire one action step. Returns one `FireResult` per bridge the step was
 * routed to (length = 1 for a single-target step, N for `"all"`).
 *
 * Never throws. Network / auth errors surface as result kinds so callers
 * can decide whether to log, toast, or stay silent.
 */
export async function fireAction(step: ActionStep): Promise<FireResult[]> {
  // Practice-clock steps never leave the browser - they drive local signals
  // in `clock/practiceClock.ts`, not Live. Intercept BEFORE resolving
  // bridges, otherwise a rig with no bridge paired would report
  // `no-bridges` for a control that needs no bridge at all (and, worse,
  // would leave the practice clock unusable in exactly the disconnected
  // rehearsal case it exists for).
  const local = applyPracticeClockStep(step);
  if (local !== null) return [local];

  const targets = resolveTargets(step.target);
  if (targets.length === 0) {
    return [{ target: step.target || '<default>', kind: 'no-bridges' }];
  }
  return Promise.all(targets.map((cfg) => fireOnBridge(step, cfg)));
}

/** Convenience: drop the result array, just count ok's. Useful for
 *  fire-and-forget callers that only care whether something landed. */
export async function fireActionFireAndForget(step: ActionStep): Promise<void> {
  await fireAction(step);
}

// ============================================================
// Stream Deck — execute an MCP action
// ============================================================

/**
 * Fire a real Stream Deck action by its MCP instance UUID, through
 * OPBridge's proxy of Elgato's MCP Deck.
 *
 * Replaces the retired `bridge.pressStreamDeck()` / `sd.press` path, which
 * was dead end-to-end: it rode the legacy Node bridge (never connected by
 * default) into a client that STUBBED the press — logging it and resolving
 * SUCCESS. The button flashed a green ack while nothing happened. That is
 * the specific failure mode this path must not reproduce, which is why an
 * unassigned action is refused here rather than dispatched hopefully.
 */
export async function executeStreamDeckAction(
  actionId: string,
  target = '',
): Promise<FireResult[]> {
  if (!actionId) {
    return [{ target: target || '<default>', kind: 'unsupported-step', detail: 'no action assigned' }];
  }
  const targets = resolveTargets(target);
  if (targets.length === 0) {
    return [{ target: target || '<default>', kind: 'no-bridges' }];
  }
  return Promise.all(targets.map((cfg) => executeStreamDeckOnBridge(actionId, cfg)));
}

async function executeStreamDeckOnBridge(
  actionId: string,
  cfg: BridgeConfig,
): Promise<FireResult> {
  const conn = getConnection(cfg.id);
  if (!conn) return { target: cfg.id, kind: 'bridge-unknown' };
  if (conn.status() !== 'connected') {
    return { target: cfg.id, kind: 'bridge-disconnected', detail: conn.status() };
  }
  try {
    const res = await conn.httpClient().executeStreamDeckAction(actionId);
    return res?.status
      ? { target: cfg.id, kind: 'ok', detail: res.status }
      : { target: cfg.id, kind: 'ok' };
  } catch (err) {
    return {
      target: cfg.id,
      kind: 'http-error',
      detail: err instanceof OpbridgeError ? `${err.kind}: ${err.message}` : String(err),
    };
  }
}

// ============================================================
// LFO engine — arm / stop
// ============================================================

/**
 * Parameters the PWA-side SineLfo already has (MIDI-shaped, 0..127
 * range for min/max/center/amplitude). Normalised to unit-range at
 * dispatch time before hitting OPBridge. Consumer key is hardcoded
 * "opbuttons" — the multi-consumer story is beyond this session.
 */
export interface ArmLfoParams {
  lfoId: string;
  ccNumber: number;
  channel: number; // 0..15
  periodBeats: number;
  /** MIDI-space center (0..127). */
  center: number;
  /** MIDI-space amplitude (half-range, 0..63.5). */
  amplitude: number;
  waveform: 'sine' | 'saw';
  loop: boolean;
}

/** Arm an LFO on OPBridge. Same target-resolution rules as fireAction
 *  (empty target = default bridge; explicit id = that bridge only;
 *  "all" fans out). Port comes from that bridge's `defaultMidiOutput`. */
export async function armLfo(params: ArmLfoParams, target = ''): Promise<FireResult[]> {
  const targets = resolveTargets(target);
  if (targets.length === 0) {
    return [{ target: target || '<default>', kind: 'no-bridges' }];
  }
  return Promise.all(targets.map((cfg) => armLfoOnBridge(params, cfg)));
}

/** Stop an LFO on OPBridge. `target` follows the same rules as {@link armLfo}. */
export async function stopLfoAction(lfoId: string, target = ''): Promise<FireResult[]> {
  const targets = resolveTargets(target);
  if (targets.length === 0) {
    return [{ target: target || '<default>', kind: 'no-bridges' }];
  }
  return Promise.all(targets.map((cfg) => stopLfoOnBridge(lfoId, cfg)));
}

async function armLfoOnBridge(
  params: ArmLfoParams,
  cfg: BridgeConfig,
): Promise<FireResult> {
  const conn = getConnection(cfg.id);
  if (!conn) return { target: cfg.id, kind: 'bridge-unknown' };
  if (conn.status() !== 'connected') {
    return { target: cfg.id, kind: 'bridge-disconnected', detail: conn.status() };
  }
  if (!cfg.defaultMidiOutput) {
    return { target: cfg.id, kind: 'port-not-configured' };
  }
  try {
    await conn.httpClient().startLfo('opbuttons', {
      id: params.lfoId,
      target: {
        type: 'midi',
        port: cfg.defaultMidiOutput,
        channel: params.channel & 0x0f,
        ccNumber: params.ccNumber & 0x7f,
      },
      periodBeats: params.periodBeats,
      // Normalise MIDI 0..127 → engine 0..1. OPBridge validates
      // amplitude ≤ 1 and center ≤ 1; center + amplitude should stay
      // within the DAW's mappable range so the sweep doesn't clip.
      amplitude: Math.max(0, Math.min(1, params.amplitude / 127)),
      center: Math.max(0, Math.min(1, params.center / 127)),
      waveform: params.waveform,
      loop: params.loop,
    });
    return { target: cfg.id, kind: 'ok' };
  } catch (err) {
    const msg = err instanceof OpbridgeError ? `${err.kind}: ${err.message}`
              : err instanceof Error           ? err.message
              : String(err);
    return { target: cfg.id, kind: 'http-error', detail: msg };
  }
}

async function stopLfoOnBridge(
  lfoId: string,
  cfg: BridgeConfig,
): Promise<FireResult> {
  const conn = getConnection(cfg.id);
  if (!conn) return { target: cfg.id, kind: 'bridge-unknown' };
  if (conn.status() !== 'connected') {
    return { target: cfg.id, kind: 'bridge-disconnected', detail: conn.status() };
  }
  try {
    await conn.httpClient().stopLfo('opbuttons', lfoId);
    return { target: cfg.id, kind: 'ok' };
  } catch (err) {
    const msg = err instanceof OpbridgeError ? `${err.kind}: ${err.message}`
              : err instanceof Error           ? err.message
              : String(err);
    return { target: cfg.id, kind: 'http-error', detail: msg };
  }
}

// ============================================================
// Sequencer engine — push library + arm (toggle)
// ============================================================

/**
 * Translate a PWA `Sequencer` to OPBridge's engine wire shape. Two gaps
 * between the schemas, both resolved here (mirrors the LFO, which injects
 * the port at dispatch time):
 *
 *  1. **Port injection.** OPBridge's `SequencerLaneTarget` REQUIRES a
 *     `port` (the MIDI output the lane fires to); the PWA lane target has
 *     no port — the bridge's `defaultMidiOutput` supplies it, same as every
 *     other MIDI dispatch on that bridge.
 *  2. **Kind casing.** OPBridge's `MidiKind` serialises camelCase
 *     (`noteOn`); the PWA stores `noteon`. `cc` is unchanged.
 *
 * Everything else (id/name/lanes/steps/lengthSteps/stepsPerBeat + the
 * reserved fields) passes through unchanged — OPBridge tolerates unknown /
 * reserved fields and only runtime-uses the ones it understands.
 */
/** Map the PWA's lowercase MIDI kind to OPBridge's camelCase `MidiKind`
 *  (`noteon`→`noteOn`, `noteoff`→`noteOff`, `cc`→`cc`). Shared by the
 *  sequencer + macro engine-library translations. */
function bridgeMidiKind(kind: string): string {
  if (kind === 'noteon') return 'noteOn';
  if (kind === 'noteoff') return 'noteOff';
  return kind;
}

/**
 * One lane target on the wire, for both engines.
 *
 * - **MIDI** keeps the legacy untagged shape (no `type`), which OPBridge
 *   still reads as `Midi` - so no existing lane changes on the wire. It needs
 *   the bridge's `port` injected.
 * - **DMX** is OPBridge's tagged `DmxLaneTarget`: `{type:'dmx', fixtureId,
 *   capability, subfixtureIndex?}`. No port - a light is addressed by fixture
 *   and capability, never by MIDI port or DMX channel (DEC-103).
 */
function laneTargetWire(
  t: SequencerMidiTarget | MacroMidiTarget | LaneDmxTarget,
  port: string,
): Record<string, unknown> {
  if (isDmxLaneTarget(t)) {
    return {
      type: 'dmx',
      fixtureId: t.fixtureId,
      capability: t.capability,
      ...(t.subfixtureIndex !== undefined ? { subfixtureIndex: t.subfixtureIndex } : {}),
    };
  }
  return {
    bridge: t.bridge,
    port,
    kind: bridgeMidiKind(t.kind),
    channel: t.channel,
    number: t.number,
  };
}

/** A pattern needs a MIDI port only if one of its lanes is MIDI. An all-light
 *  sequencer or macro must not be refused for the missing port it never uses. */
function lanesNeedMidiPort(lanes: Array<{ target: SequencerMidiTarget | MacroMidiTarget | LaneDmxTarget }>): boolean {
  return lanes.some((l) => !isDmxLaneTarget(l.target));
}

function toBridgeSequencer(seq: Sequencer, port: string): Record<string, unknown> {
  return {
    ...seq,
    lanes: seq.lanes.map((lane) => ({ ...lane, target: laneTargetWire(lane.target, port) })),
  };
}

/**
 * Push one sequencer's current definition into OPBridge's engine library
 * (upsert via `PUT /sequencers/opbuttons/{id}`). Call this before arming so
 * the engine steps the latest pattern — it's idempotent, so pushing on
 * every arm tap keeps live step-edits in sync with zero extra bookkeeping.
 * Same primary-only, never-throws contract as {@link armLfo}; needs the
 * bridge's `defaultMidiOutput` for the injected lane port.
 */
export async function pushSequencer(seq: Sequencer, target = ''): Promise<FireResult[]> {
  const targets = resolveTargets(target);
  if (targets.length === 0) {
    return [{ target: target || '<default>', kind: 'no-bridges' }];
  }
  return Promise.all(targets.map((cfg) => pushSequencerOnBridge(seq, cfg)));
}

async function pushSequencerOnBridge(seq: Sequencer, cfg: BridgeConfig): Promise<FireResult> {
  const conn = getConnection(cfg.id);
  if (!conn) return { target: cfg.id, kind: 'bridge-unknown' };
  if (conn.status() !== 'connected') {
    return { target: cfg.id, kind: 'bridge-disconnected', detail: conn.status() };
  }
  if (!cfg.defaultMidiOutput && lanesNeedMidiPort(seq.lanes)) {
    return { target: cfg.id, kind: 'port-not-configured' };
  }
  try {
    const wire = toBridgeSequencer(seq, cfg.defaultMidiOutput ?? '');
    await conn.httpClient().putSequencer('opbuttons', seq.id, wire as { id: string });
    return { target: cfg.id, kind: 'ok' };
  } catch (err) {
    const msg = err instanceof OpbridgeError ? `${err.kind}: ${err.message}`
              : err instanceof Error           ? err.message
              : String(err);
    return { target: cfg.id, kind: 'http-error', detail: msg };
  }
}

/**
 * Arm (toggle) a sequencer on OPBridge's engine. First tap → armed
 * (fires on the next downbeat of a playing clock); tap again while
 * armed/running → the engine flips it to Killing and drains hanging
 * note-offs. The pattern must already be pushed under the same bearer —
 * {@link pushSequencer} handles that. Same primary-only, never-throws
 * contract as {@link armLfo}. No MIDI port needed here (the pushed lanes
 * already carry it).
 */
export async function armSequencerAction(sequencerId: string, target = ''): Promise<FireResult[]> {
  const targets = resolveTargets(target);
  if (targets.length === 0) {
    return [{ target: target || '<default>', kind: 'no-bridges' }];
  }
  return Promise.all(targets.map((cfg) => armSequencerOnBridge(sequencerId, cfg)));
}

async function armSequencerOnBridge(sequencerId: string, cfg: BridgeConfig): Promise<FireResult> {
  const conn = getConnection(cfg.id);
  if (!conn) return { target: cfg.id, kind: 'bridge-unknown' };
  if (conn.status() !== 'connected') {
    return { target: cfg.id, kind: 'bridge-disconnected', detail: conn.status() };
  }
  try {
    await conn.httpClient().armSequencer('opbuttons', sequencerId);
    return { target: cfg.id, kind: 'ok' };
  } catch (err) {
    const msg = err instanceof OpbridgeError ? `${err.kind}: ${err.message}`
              : err instanceof Error           ? err.message
              : String(err);
    return { target: cfg.id, kind: 'http-error', detail: msg };
  }
}

// ============================================================
// Macro engine — push library + arm (toggle)
// ============================================================

/**
 * Translate a PWA `Macro` to OPBridge's engine wire shape. Same two gaps as
 * the sequencer ({@link toBridgeSequencer}) — OPBridge's `MacroLaneTarget`
 * requires a `port` (injected from the bridge's `defaultMidiOutput`) and its
 * `MidiKind` is camelCase. Everything else (name / triggerLengthBeats / lane
 * shape / center / amplitude / lengthBeats) matches 1:1 — OPBridge's shape
 * evaluator is a direct port of `@opbuttons/schema`'s.
 */
function toBridgeMacro(macro: Macro, port: string): Record<string, unknown> {
  return {
    ...macro,
    lanes: macro.lanes.map((lane) => ({ ...lane, target: laneTargetWire(lane.target, port) })),
  };
}

/** Push one macro's current definition into OPBridge's engine library
 *  (`PUT /macros/opbuttons/{id}`). Idempotent — push on every arm tap to keep
 *  live edits in sync. Same primary-only, never-throws contract as
 *  {@link pushSequencer}; needs the bridge's `defaultMidiOutput`. */
export async function pushMacro(macro: Macro, target = ''): Promise<FireResult[]> {
  const targets = resolveTargets(target);
  if (targets.length === 0) {
    return [{ target: target || '<default>', kind: 'no-bridges' }];
  }
  return Promise.all(targets.map((cfg) => pushMacroOnBridge(macro, cfg)));
}

async function pushMacroOnBridge(macro: Macro, cfg: BridgeConfig): Promise<FireResult> {
  const conn = getConnection(cfg.id);
  if (!conn) return { target: cfg.id, kind: 'bridge-unknown' };
  if (conn.status() !== 'connected') {
    return { target: cfg.id, kind: 'bridge-disconnected', detail: conn.status() };
  }
  if (!cfg.defaultMidiOutput && lanesNeedMidiPort(macro.lanes)) {
    return { target: cfg.id, kind: 'port-not-configured' };
  }
  try {
    const wire = toBridgeMacro(macro, cfg.defaultMidiOutput ?? '');
    await conn.httpClient().putMacro('opbuttons', macro.id, wire as { id: string });
    return { target: cfg.id, kind: 'ok' };
  } catch (err) {
    const msg = err instanceof OpbridgeError ? `${err.kind}: ${err.message}`
              : err instanceof Error           ? err.message
              : String(err);
    return { target: cfg.id, kind: 'http-error', detail: msg };
  }
}

/** Arm (toggle) a macro on OPBridge's engine. First tap → armed (fires on
 *  the next downbeat of a playing clock); tap again → Killing (each lane
 *  snaps to center). The pattern must already be pushed under the same
 *  bearer ({@link pushMacro}). Same contract as {@link armSequencerAction}. */
export async function armMacroAction(macroId: string, target = ''): Promise<FireResult[]> {
  const targets = resolveTargets(target);
  if (targets.length === 0) {
    return [{ target: target || '<default>', kind: 'no-bridges' }];
  }
  return Promise.all(targets.map((cfg) => armMacroOnBridge(macroId, cfg)));
}

async function armMacroOnBridge(macroId: string, cfg: BridgeConfig): Promise<FireResult> {
  const conn = getConnection(cfg.id);
  if (!conn) return { target: cfg.id, kind: 'bridge-unknown' };
  if (conn.status() !== 'connected') {
    return { target: cfg.id, kind: 'bridge-disconnected', detail: conn.status() };
  }
  try {
    await conn.httpClient().armMacro('opbuttons', macroId);
    return { target: cfg.id, kind: 'ok' };
  } catch (err) {
    const msg = err instanceof OpbridgeError ? `${err.kind}: ${err.message}`
              : err instanceof Error           ? err.message
              : String(err);
    return { target: cfg.id, kind: 'http-error', detail: msg };
  }
}

// ============================================================
// Toggle engine — push definition + flip (stateful, 2026-08-16)
// ============================================================
//
// Replaces FireButton's old client-only toggle mode (a local `on()` signal
// flipped optimistically, corrected only by a legacy-bridge `midi.in`
// echo). State now lives on OPBridge so it survives the tab closing — the
// whole point of the 2026-08-05 ask ("gotta be robust as much as
// possible... i need to read this status for other apps"). Wire shape
// byte-verified live 2026-08-16 (PUT + GET round-trip against the real
// dev-PC daemon, no flip triggered during the probe to avoid a live send).
//
// onAction/offAction use `sendRaw` with literal MIDI bytes, NOT the
// `send`/`kind:"cc"` shape the Timeline Cue ask showed for Program Change —
// `sendRaw` is the one CC-shaped action already byte-verified end-to-end in
// this codebase (`ruleSync.ts::panicRule`'s 48-action storm, which ran on it
// until 2026-09-23 when Panic moved to `panic.fire`; confirmed against
// `dispatch.ts::panicOnBridge` + OPBridge's own `build_midi_bytes` unit
// tests). A `send`/`kind:"cc"` args shape was never independently confirmed
// to actually parse at flip time (my probe only proved PUT accepts it as a
// well-formed JSON envelope, not that dispatch understands those specific
// field names) — not worth gambling on for a stage-fired control.

export interface ToggleActionConfig {
  actionType: 'cc' | 'noteon';
  /** 0-based, matches every other channel field in this file. */
  channel: number;
  ccNumber?: number;
  onValue?: number;
  /** CC value for the OFF side. Absent = 0 (the previous hardcoded value). */
  offValue?: number;
  note?: number;
  velocity?: number;
}

/** Exported since 2026-08-28 so `midiTrigger/ruleSync.ts` can resolve a
 *  trigger-mode Button binding to the SAME bytes a finger tap sends. Reused
 *  rather than re-derived deliberately: a second copy of this byte logic is
 *  exactly how a pedal press and a tap drift apart, and the Timeline release
 *  path already carries a "must stay in agreement" comment for the same
 *  reason. */
export function toggleActions(cfg: ToggleActionConfig, port: string): { onAction: RuleAction; offAction: RuleAction } {
  const ch = cfg.channel & 0x0f;
  if (cfg.actionType === 'noteon') {
    const note = (cfg.note ?? 60) & 0x7f;
    const vel = (cfg.velocity ?? 127) & 0x7f;
    return {
      onAction: { module: 'midi', command: 'sendRaw', args: { port, bytes: [0x90 | ch, note, vel] } },
      offAction: { module: 'midi', command: 'sendRaw', args: { port, bytes: [0x80 | ch, note, 0] } },
    };
  }
  const cc = (cfg.ccNumber ?? 0) & 0x7f;
  const onVal = (cfg.onValue ?? 127) & 0x7f;
  const offVal = (cfg.offValue ?? 0) & 0x7f;
  return {
    onAction: { module: 'midi', command: 'sendRaw', args: { port, bytes: [0xb0 | ch, cc, onVal] } },
    offAction: { module: 'midi', command: 'sendRaw', args: { port, bytes: [0xb0 | ch, cc, offVal] } },
  };
}

/** Push one toggle's current on/off actions into OPBridge's engine library
 *  (`PUT /toggles/opbuttons/{id}`).
 *
 *  ⚠️ Do NOT call this before every flip, unlike {@link pushSequencer}'s
 *  "push on every arm tap" contract. Byte-verified live 2026-08-16: PUT is a
 *  FULL REPLACE of the `{onAction, offAction, state}` record — a toggle's
 *  `state` lives in the SAME resource its definition does (unlike a
 *  sequencer/macro, whose runtime state is a separate resource from its
 *  library entry). A body with no `state` field silently resets `state` to
 *  `false`, so pushing right before every flip makes every tap after the
 *  first a no-op false→true flip forever — caught by
 *  `fire_button_taps_cc.ts`'s "tap again" step, not guessed. Callers should
 *  push reactively when the button's CONFIG changes (debounced, mirroring
 *  `midiTrigger/ruleSync.ts`) and let {@link flipToggleAction}'s
 *  `notFound` result drive a one-time self-heal push, never an
 *  unconditional one at fire time. */
export async function pushToggle(
  toggleId: string,
  name: string | undefined,
  cfg: ToggleActionConfig,
  target = '',
): Promise<FireResult[]> {
  const targets = resolveTargets(target);
  if (targets.length === 0) {
    return [{ target: target || '<default>', kind: 'no-bridges' }];
  }
  return Promise.all(targets.map((bridgeCfg) => pushToggleOnBridge(toggleId, name, cfg, bridgeCfg)));
}

/** One `GET /toggles/opbuttons` per connection at a time: ruleSync pushes every
 *  bound toggle at once, and they share the read rather than fetching the whole
 *  library once each. */
const toggleLibraryReads = new WeakMap<BridgeConnection, Promise<ToggleState[] | null>>();

/** What OPBridge holds for this toggle id, or undefined when it holds nothing
 *  (or the read failed: then the caller pushes as before, and the flip's own
 *  readback stays the source of truth). */
async function heldToggle(conn: BridgeConnection, toggleId: string): Promise<ToggleState | undefined> {
  let read = toggleLibraryReads.get(conn);
  if (!read) {
    read = conn.httpClient().getToggles('opbuttons')
      .then((lib) => lib.toggles)
      .catch(() => null)
      .finally(() => { toggleLibraryReads.delete(conn); });
    toggleLibraryReads.set(conn, read);
  }
  return (await read)?.find((t) => t.id === toggleId);
}

/** Same name and actions, compared key-order-blind (OPBridge returns its JSON
 *  key-sorted). */
function sameToggleDefinition(
  held: ToggleState,
  body: { name?: string; onAction: RuleAction; offAction: RuleAction },
): boolean {
  return (held.name ?? '') === (body.name ?? '')
    && canonicalJson(held.onAction) === canonicalJson(body.onAction)
    && canonicalJson(held.offAction) === canonicalJson(body.offAction);
}

async function pushToggleOnBridge(
  toggleId: string,
  name: string | undefined,
  cfg: ToggleActionConfig,
  bridgeCfg: BridgeConfig,
): Promise<FireResult> {
  const conn = getConnection(bridgeCfg.id);
  if (!conn) return { target: bridgeCfg.id, kind: 'bridge-unknown' };
  if (conn.status() !== 'connected') {
    return { target: bridgeCfg.id, kind: 'bridge-disconnected', detail: conn.status() };
  }
  if (!bridgeCfg.defaultMidiOutput) {
    return { target: bridgeCfg.id, kind: 'port-not-configured' };
  }
  try {
    const { onAction, offAction } = toggleActions(cfg, bridgeCfg.defaultMidiOutput);
    const body: { id: string; name?: string; onAction: RuleAction; offAction: RuleAction; state?: boolean } =
      name ? { id: toggleId, name, onAction, offAction } : { id: toggleId, onAction, offAction };
    // The PUT is a full replace and OPBridge defaults a missing `state` to
    // false, so a plain re-push turned an ON toggle OFF in OPBridge with no
    // OFF sent (2026-11-03 audit A-1: every remount and every ruleSync pass
    // re-pushed). Read what OPBridge holds first: an unchanged definition
    // needs no PUT at all, and a changed one keeps the current state.
    const held = await heldToggle(conn, toggleId);
    if (held) {
      if (sameToggleDefinition(held, body)) return { target: bridgeCfg.id, kind: 'ok' };
      body.state = held.state;
    }
    await conn.httpClient().putToggle('opbuttons', toggleId, body);
    return { target: bridgeCfg.id, kind: 'ok' };
  } catch (err) {
    const msg = err instanceof OpbridgeError ? `${err.kind}: ${err.message}`
              : err instanceof Error           ? err.message
              : String(err);
    return { target: bridgeCfg.id, kind: 'http-error', detail: msg };
  }
}

export interface ToggleFlipResult extends FireResult {
  state?: boolean;
  /** True when the failure was specifically a 404 (this id isn't in
   *  OPBridge's toggle library yet) — the ONE case worth telling apart from
   *  a generic error, since the caller can self-heal it by pushing once and
   *  retrying, rather than surfacing a dead button. */
  notFound?: boolean;
}

/** Flip a toggle on OPBridge — fires whichever of onAction/offAction now
 *  applies server-side and returns the new persisted state. The toggle must
 *  already be pushed under the same bearer ({@link pushToggle}) or this
 *  404s (`notFound: true` on the result — the caller's job to push-then-
 *  retry, NOT this function's: pushing here unconditionally is the exact
 *  bug this file used to have, see {@link pushToggle}'s doc comment).
 *  Fan-out follows the same target-resolution rules as every other dispatch
 *  function here; each result carries ITS OWN bridge's resulting `state` —
 *  a multi-bridge toggle is N independent per-bridge booleans (each bridge
 *  owns its own toggle library), not one shared state. The caller
 *  reconciles across targets same as it already does for every other
 *  multi-target fire. */
export async function flipToggleAction(toggleId: string, target = ''): Promise<ToggleFlipResult[]> {
  const targets = resolveTargets(target);
  if (targets.length === 0) {
    return [{ target: target || '<default>', kind: 'no-bridges' }];
  }
  return Promise.all(targets.map((cfg) => flipToggleOnBridge(toggleId, cfg)));
}

async function flipToggleOnBridge(toggleId: string, cfg: BridgeConfig): Promise<ToggleFlipResult> {
  const conn = getConnection(cfg.id);
  if (!conn) return { target: cfg.id, kind: 'bridge-unknown' };
  if (conn.status() !== 'connected') {
    return { target: cfg.id, kind: 'bridge-disconnected', detail: conn.status() };
  }
  try {
    const res = await conn.httpClient().flipToggle('opbuttons', toggleId);
    return { target: cfg.id, kind: 'ok', state: res.state };
  } catch (err) {
    if (err instanceof OpbridgeError && err.httpStatus === 404) {
      return { target: cfg.id, kind: 'http-error', detail: 'not found', notFound: true };
    }
    const msg = err instanceof OpbridgeError ? `${err.kind}: ${err.message}`
              : err instanceof Error           ? err.message
              : String(err);
    return { target: cfg.id, kind: 'http-error', detail: msg };
  }
}

// ============================================================
// Transpose — OSC to the OP-AbletonTranspose M4L device
// ============================================================

/** OSC address the OP-AbletonTranspose device listens on (transpose.js).
 *  Raw semitone as a single int arg. */
export const TRANSPOSE_OSC_ADDRESS = '/OP-AbletonTranspose/transpose';
/** UDP target of the device's udpreceive — fixed on the OPBridge host
 *  (send-transpose-test.mjs / PROTOCOL.md TRANSPOSE_TARGET). */
export const TRANSPOSE_OSC_TARGET = '127.0.0.1:11220';

/**
 * Send a transpose (raw semitones, clamped -12..+12) to the
 * OP-AbletonTranspose device via OPBridge's `POST /osc/send` on the primary
 * bridge. No MIDI port needed — it's an OSC send. Same target-resolution +
 * never-throws contract as {@link fireAction}. The `/osc/send` scope is in
 * the `opbuttons-full` bundle, so a paired bearer already covers it.
 */
export async function sendTranspose(semitones: number, target = ''): Promise<FireResult[]> {
  const targets = resolveTargets(target);
  if (targets.length === 0) {
    return [{ target: target || '<default>', kind: 'no-bridges' }];
  }
  const n = Math.max(-12, Math.min(12, Math.round(semitones)));
  return Promise.all(targets.map((cfg) => sendTransposeOnBridge(n, cfg)));
}

async function sendTransposeOnBridge(semitones: number, cfg: BridgeConfig): Promise<FireResult> {
  const conn = getConnection(cfg.id);
  if (!conn) return { target: cfg.id, kind: 'bridge-unknown' };
  if (conn.status() !== 'connected') {
    return { target: cfg.id, kind: 'bridge-disconnected', detail: conn.status() };
  }
  try {
    await conn.httpClient().oscSend(TRANSPOSE_OSC_ADDRESS, [semitones], TRANSPOSE_OSC_TARGET);
    return { target: cfg.id, kind: 'ok' };
  } catch (err) {
    const msg = err instanceof OpbridgeError ? `${err.kind}: ${err.message}`
              : err instanceof Error           ? err.message
              : String(err);
    return { target: cfg.id, kind: 'http-error', detail: msg };
  }
}

// ============================================================
// Scene Copy — OSC to the OP-AbletonSceneCopy device (UDP 11240)
// ============================================================
//
// Copies a row of Session clips from one scene into another without
// duplicating the scene (which would renumber scenes below it and break
// per-scene MIDI mappings). Two verbs, one stateful pair: COPY arms a
// scene into the device's clipboard, PASTE writes it into another.
//
// Rides the same `POST /osc/send` path as Transpose, the Mixer and Ableton
// Control, so NO OPBridge work is owed — `/osc/send` passes `target`
// straight through and is gated only on the scope `opbuttons-full` grants.
// 11240 continues the device port decade pattern (11200 clock out, 11210
// sync in, 11220 transpose in, 11230 mixer in).
//
// ⚠️ NUMBERING. `scene` here is 1-BASED (what Live draws, what the buttons
// are labelled). The wire is 0-BASED (the LOM's convention, and the
// device's own documented WIRE CONTRACT). The `scene - 1` below is the ONLY
// place that conversion happens in the PWA — mirroring `disp()` being the
// only bridge on the device side. Do not "fix" it at a call site.

/** OSC addresses the device listens on. Explicit-index verbs, NOT the bare
 *  `/copy` + `/paste` ones: those act on whatever scene Live currently has
 *  SELECTED, which is exactly the hidden state a labelled button surface
 *  exists to avoid.
 *
 *  ⚠️ THE FILE NAME AND THE ADDRESS PREFIX DISAGREE, DELIBERATELY. The
 *  device ships as `OP-SceneCopyPaste.amxd` (renamed from
 *  `OP-AbletonSceneCopy` 2026-08-30) but its JS still declares
 *  `var OSC = "/OP-AbletonSceneCopy"`, which is what it uses for its own
 *  OUTBOUND messages. Inbound, the device checks ONLY THE LAST PATH SEGMENT
 *  and ignores the prefix entirely - its own header says `/copy` and
 *  `/scenecopy/copy` work identically - so this address is correct against
 *  either name. It is kept matching the device's declared `OSC` var rather
 *  than the file name, because that is the string a future version would
 *  compare against if it ever did start checking the prefix. Verified
 *  against the renamed build: `udpreceive 11240` and both verbs unchanged,
 *  JS byte-identical to the staging copy. */
export const SCENE_COPY_OSC_ADDRESS = '/OP-AbletonSceneCopy/copy_scene';
export const SCENE_PASTE_OSC_ADDRESS = '/OP-AbletonSceneCopy/paste_scene';
/**
 * CLEAR (device v0.4). Empties every clip slot on a scene, across every
 * track. DESTRUCTIVE, and the most destructive verb this app can send.
 *
 * It does NOT remove the scene. That distinction is the device's entire
 * reason for existing - Live's own scene delete/duplicate renumbers
 * everything below it and breaks per-scene MIDI mappings, whereas this only
 * empties slots that already exist, so scene count and order are invariant.
 * "Delete scene" would be the wrong mental model and the wrong verb.
 *
 * v0.4 introduced this to REPLACE mirror mode, which used to delete target
 * clips implicitly wherever the source row was empty. The device's own note:
 * mirror "was a stored, MIDI-mappable front-panel toggle that could silently
 * destroy clips if it was ever left on". CLEAR is the same capability made
 * explicit and visible - to mirror a scene you now CLEAR the target, then
 * COPY and PASTE, and the destruction is in the button presses.
 */
export const SCENE_CLEAR_OSC_ADDRESS = '/OP-AbletonSceneCopy/clear_scene';
/**
 * DELETE ALL (device v1.2). Empties every clip slot on EVERY scene. This is
 * the single most destructive message OPButtons can put on a wire, and it is
 * the only one whose safety does not live in this app.
 *
 * TWO PRESSES, AND THE GUARD IS THE DEVICE'S. The first `clear_all` ARMS and
 * deletes nothing; a second within the device's 3s window executes; a lapse
 * disarms on its own. The arm lives in the device's JS because all three
 * paths to this action converge there - its panel button, a MIDI mapping on
 * that button, and this relayed packet - so a guard in our UI would not have
 * protected the wire. Verified against `OPM4LBuilder/staging/CopyPasteDelete.js`
 * (`ALL_ARM_MS = 3000`, `doClearAll`), not taken from the ask.
 *
 * ⚠️ THE OSC PATH WAS NOT DEBOUNCED IN rc1, AND IS IN rc2. This is the one
 * device fact on which our own timing depends, so it is worth the paragraph.
 *
 * In `v1.2-rc1` the 250ms `debounced()` gate was applied to `clear_all_btn`
 * (the panel button) and the slot buttons, while the `clear_all` verb this
 * address maps to reached `doClearAll` un-gated - so two packets 5ms apart
 * armed and then executed, and the ask's "a double-send is not a workaround"
 * was false on this path. We measured it (remove our gap, one double-tap puts
 * 2 packets on the wire, the second an execute) and reported it.
 * **OPM4LBuilder fixed it in `v1.2-rc2`**: the relayed verb now shares the
 * panel button's `"xall"` debounce key, so the button, its MIDI mapping and
 * our packet are one logical control. Chasing it also turned up a third fault
 * we had not seen - a duplicated EXECUTE packet silently RE-ARMED the device,
 * so the operator saw the delete land and the device stayed live for another
 * one with our bar dark throughout. Same gate closes it.
 *
 * **The CANCEL is deliberately NOT debounced, and that is load-bearing for
 * us.** Gating `doClearAll` wholesale would have swallowed our `clear_all 0`,
 * which we send immediately after a confirm - leaving the device armed behind
 * a dark bar, which is the exact state {@link sendSceneClearAll}'s invariant
 * exists to prevent and which we cannot see. Arm and execute are gated; the
 * withdraw is not.
 *
 * See {@link SCENE_CLEAR_ALL_MIN_GAP_MS} for what rc2 means for our own
 * number - it changed WHY that constant matters, not its value.
 *
 * `intent` maps to the device's own argument: `press` sends `1` (identical to
 * the bare `clear_all` the ask documents - `doClearAll(a.length ? intArg(a, 0, 1) : 1)`
 * collapses both to 1) and `cancel` sends `0`, the device's explicit
 * withdraw-an-arm verb. We send `[1]` rather than `[]` deliberately: no call
 * site in this app has ever put a zero-length arg list through OPBridge's OSC
 * encoder, and the most destructive message in the suite is the wrong place
 * to find out how it handles one.
 */
export const SCENE_CLEAR_ALL_OSC_ADDRESS = '/OP-AbletonSceneCopy/clear_all';

/** UDP target of the device's udpreceive (its documented WIRE CONTRACT).
 *  Unchanged by the v1.2 rename to OP-CopyPasteDelete, verified against
 *  its `udpreceive 11240`. */
export const SCENE_COPY_OSC_TARGET = '127.0.0.1:11240';

/**
 * Arm (`'copy'`) or write (`'paste'`) a Live scene row on the
 * OP-AbletonSceneCopy device. `scene` is 1-based; the 0-based wire
 * conversion happens here and nowhere else.
 *
 * Same target-resolution + never-throws contract as {@link sendTranspose}:
 * an offline or unknown bridge yields a FireResult describing why rather
 * than throwing, so a dead device can never break a gig surface.
 */
export async function sendSceneCopy(
  verb: 'copy' | 'paste' | 'clear',
  scene: number,
  target = '',
): Promise<FireResult[]> {
  const targets = resolveTargets(target);
  if (targets.length === 0) {
    return [{ target: target || '<default>', kind: 'no-bridges' }];
  }
  // Guard the wire, not just the UI: a 0 or negative scene would send a
  // negative index and earn a `/error bad_scene_index` from the device.
  const idx = Math.max(0, Math.round(scene) - 1);
  const address = verb === 'copy'  ? SCENE_COPY_OSC_ADDRESS
                : verb === 'paste' ? SCENE_PASTE_OSC_ADDRESS
                :                    SCENE_CLEAR_OSC_ADDRESS;
  return Promise.all(targets.map((cfg) => sendSceneCopyOnBridge(address, idx, cfg)));
}

async function sendSceneCopyOnBridge(
  address: string,
  idx: number,
  cfg: BridgeConfig,
): Promise<FireResult> {
  const conn = getConnection(cfg.id);
  if (!conn) return { target: cfg.id, kind: 'bridge-unknown' };
  if (conn.status() !== 'connected') {
    return { target: cfg.id, kind: 'bridge-disconnected', detail: conn.status() };
  }
  try {
    await conn.httpClient().oscSend(address, [idx], SCENE_COPY_OSC_TARGET);
    return { target: cfg.id, kind: 'ok' };
  } catch (err) {
    const msg = err instanceof OpbridgeError ? `${err.kind}: ${err.message}`
              : err instanceof Error           ? err.message
              : String(err);
    return { target: cfg.id, kind: 'http-error', detail: msg };
  }
}

/**
 * Shortest gap we will allow between the arming press and the executing one.
 *
 * ⚠️ **THIS IS NOW A FLOOR, NOT JUST A COURTESY. DO NOT TUNE IT BELOW 250.**
 *
 * It was written when the device did NOT debounce the relayed verb, so it was
 * the only thing standing between a fat-fingered double-tap and an emptied
 * set - "enforce the gap the device is documented to enforce". `v1.2-rc2`
 * closed that hole device-side, which inverts the reasoning: the device now
 * **swallows** a confirm arriving less than 250ms after the arm. A confirm is
 * legal in the window **[250ms, 3000ms]** after the arm - under the floor it
 * is read as a double-tap, over the ceiling the arm has lapsed.
 *
 * So 300 is no longer "250 plus a little margin for safety"; it is 250 plus a
 * little margin **so the control still works**. Drop it below 250 chasing
 * responsiveness and the bar goes DEAD - every confirm swallowed device-side,
 * with no error, no packet rejected and nothing in our UI to show for it.
 * Raising it is harmless as long as it stays under `DELETE_ALL_UI_ARM_MS`
 * (2400), which is itself under the device's 3000 ceiling.
 *
 * ⚠️ **THE 50ms OF FLOOR MARGIN IS THINNER THAN 50ms.** We measure SEND to
 * SEND; the device measures ARRIVAL to ARRIVAL. Relay or network jitter
 * compresses the interval in between, so a gap that is a legal 300ms here can
 * land under 250ms there. The ceiling margin (2400 vs 3000) is 600ms and
 * genuinely comfortable; this end is the thin one. OPM4LBuilder raised this
 * and it is their observation, not ours.
 *
 * **It fails SAFE, and that is asserted rather than assumed** - their suite
 * covers arm, confirm compressed to 240ms, then our trailing cancel: the
 * confirm is swallowed so nothing is deleted, and our `clear_all 0` (never
 * debounced) disarms the device. End state is a visible no-op - two taps,
 * nothing happens, no set left armed behind a dark bar. So the 50ms does not
 * need widening for SAFETY. If it is ever widened, widen it for the reason
 * below, and say so.
 *
 * **The reason, if anyone ever does:** the two gates fail differently and
 * OURS IS THE GENTLER ONE. A tap swallowed here leaves the arm visibly up
 * with its fuse still running, so the operator's next tap completes the
 * delete. A tap swallowed device-side takes our trailing cancel with it - bar
 * dark, nothing deleted, arm gone, start again. Widening this constant past
 * the floor plus realistic jitter would make the gentler failure always win.
 * NOT done here, because it would mean retuning deployed timing on the one
 * control that empties a set without having measured the jitter it is meant
 * to absorb - and the current value is safe either way.
 *
 * **If that jitter ever needs measuring, ask OPM4LBuilder - it is a small
 * build on their side and impossible on ours.** We cannot see the arrival
 * side at all; they can stamp arrivals and emit deltas on a debug address.
 * They have deliberately NOT built it, on the same reasoning that stopped us
 * widening: new wire surface on a destructive device that has not been in
 * Ableton once. So the question has a known route to an answer and nobody
 * needs to re-derive that it is unanswerable from here - it is answerable,
 * just not yet worth the surface.
 *
 * One device-side behaviour that makes mashing harmless: a swallowed press
 * does NOT extend the lockout. `debounced()` records only ACCEPTED presses,
 * so a burst of fast taps cannot hold the gate shut and make the bar feel
 * dead.
 *
 * Keeping our own gate rather than deferring to the device's is deliberate:
 * ours swallows the tap BEFORE it reaches the wire, so a fat double-tap
 * leaves the arm visibly up rather than relying on a silent device-side drop
 * we cannot observe. Belt and braces on the one action that empties a set.
 */
export const SCENE_CLEAR_ALL_MIN_GAP_MS = 300;

/**
 * Send one DELETE ALL press (`intent: 'press'`) or withdraw an arm
 * (`intent: 'cancel'`) to the OP-CopyPasteDelete device.
 *
 * Same never-throws contract as {@link sendSceneCopy}: an offline or unknown
 * bridge yields a FireResult describing why rather than throwing.
 *
 * ⚠️ CALLERS MUST NOT CALL THIS TWICE TO "DO" A DELETE. Two presses is the
 * device's guard, not a protocol handshake to be automated away - see the
 * address doc above. The UI press-then-confirm state machine lives in
 * `components/SceneCopy.tsx`.
 */
export async function sendSceneClearAll(
  intent: 'press' | 'cancel',
  target = '',
): Promise<FireResult[]> {
  const targets = resolveTargets(target);
  if (targets.length === 0) {
    return [{ target: target || '<default>', kind: 'no-bridges' }];
  }
  const arg = intent === 'cancel' ? 0 : 1;
  return Promise.all(
    targets.map((cfg) => sendSceneCopyOnBridge(SCENE_CLEAR_ALL_OSC_ADDRESS, arg, cfg)),
  );
}

// ============================================================
// Ableton Control — OSC to the OP-AbletonSync device (UDP 11210)
// ============================================================
//
// Six controls on one device: tempo, time signature, transport, record
// quantization, launch quantization, metronome. All ride OPBridge's
// `POST /osc/send`, the same path Transpose and the Mixer already use, so
// NO OPBridge work is owed for any of this — confirmed with them
// 2026-08-27 (`OPApps/opbridge-opbuttons.md`).
//
// ⚠️ CORRECTED 2026-09-14. This used to continue: "`/osc/send` has no target
// allowlist; it passes `target` straight through, gated only on the
// `/osc/send` scope that `opbuttons-full` already grants." **That is FALSE.**
// OPBridge refuses EVERY non-loopback target with HTTP 422 - measured
// 2026-09-12 by controlled comparison, same address and args, only the host
// varying: `127.0.0.1` delivered, this machine's own LAN address did not.
// The full measurement is beside `ABLETON_SYNC_RECEIVE_TARGET` further down.
//
// CP-134 corrected it there and left this copy standing, so the false claim
// was still the FIRST thing a reader met. Two records of one fact is how the
// wrong one stays readable; this is now a pointer, not a second assertion.
//
// Verified end-to-end on the operator's live rig 2026-08-26: time signature
// 4/4 -> 3/4 -> 7/8 -> 4/4, each step confirmed through the `/clock/status`
// readback rather than assumed from a 200.
//
// ⚠️ Addresses are STATIC against the v3.13 device. We deliberately do NOT
// gate on `device_version` — OPBridge withdrew that advice on OPM4LBuilder's
// argument that it is version archaeology. The intended signal is a failed
// round-trip verify, which catches a wrong address, an old device AND a dead
// wire; a version check catches only one of the three. If any of these
// addresses is renamed device-side, the round-trip smoke fails, which is the
// point.

/** UDP target of the OP-AbletonSync device's `udpreceive`, on the OPBridge
 *  host. Distinct from 11200, which is the device's OUTBOUND feed that
 *  OPBridge itself binds (we can never read that socket directly). */
export const ABLETON_SYNC_TARGET = '127.0.0.1:11210';

/**
 * UDP target of `OP-AbletonSyncReceive`. **Loopback, like every other
 * address in this file** - but loopback relative to THE BRIDGE THAT FIRES
 * THE STEP, which is the whole mechanism here.
 *
 * The device runs on the streampc slave, not on the master. We reach it the
 * way the rest of the app reaches everything: the PWA speaks the OPBridge
 * protocol across the LAN to the OPBridge running on that machine, and THAT
 * bridge emits the OSC over its own loopback to the device beside it
 * (DEC-169 - the PWA connects to every bridge on the mesh directly and
 * equally, and a button picks its bridge with `bridgeIds`). The protocol
 * crosses the network; the OSC never does.
 *
 * ⚠️ WHY NOT JUST SEND TO `192.168.50.50:11260` FROM THE MASTER'S BRIDGE.
 * Because OPBridge will not do it. `POST /osc/send` refuses every
 * non-loopback target with **HTTP 422** - measured 2026-09-12 against
 * v1.15.125+96693d9 by controlled comparison, same address and empty args,
 * only the target host varying:
 *
 * ```
 *   127.0.0.1:11261       200  {"bytes_sent":36,"ok":true}   datagram received
 *   192.168.50.51:11261   422  send OSC to 192.168.50.51:11261   nothing sent
 *   192.168.50.50:11260   422  send OSC to 192.168.50.50:11260   nothing sent
 * ```
 *
 * 192.168.50.51 is this machine's own LAN address, so the refusal is on the
 * address being non-loopback, not on the host being remote or unreachable.
 * (The listener was proven able to receive first, so the zero is the
 * relay's and not the probe's.) That also corrects the `/osc/send` comment
 * further up this file, which claimed the route "has no target allowlist"
 * and "passes `target` straight through" - true of every path it was
 * written for, all of which are loopback, and never once exercised across
 * the LAN.
 *
 * ⚠️⚠️ THE COST OF LOOPBACK: A MISROUTED SEND IS SILENT. A datagram to
 * 127.0.0.1:11260 on a machine with no `OP-AbletonSyncReceive` is accepted
 * by OPBridge, reported as `{ok:true}`, and lands nowhere - UDP tells
 * nobody. That is why {@link sendAbletonResync} REFUSES a step that has not
 * been aimed at a specific bridge: "default bridge" means the master, and
 * on the master this address is a hole in the ground. The refusal is the
 * only loud signal available once the 422 is gone.
 */
export const ABLETON_SYNC_RECEIVE_TARGET = '127.0.0.1:11260';

export const ABLETON_TEMPO_ADDRESS = '/OP-BridgeSync/set_tempo';
export const ABLETON_TIME_SIG_ADDRESS = '/OP-BridgeSync/set_time_sig';
export const ABLETON_REC_QUANT_ADDRESS = '/OP-BridgeSync/set_rec_quant';

/** `OP-Quantize` (OPM4LBuilder, wire frozen for v0.1, 2026-10-10): a soft
 *  record quantize, one instance per track, all on `udpreceive 11290` on the
 *  Ableton machine's loopback. `set_grid` takes the same nine labels as
 *  `set_rec_quant`; `set_amount` an int percent. Its `/OP-Quantize/state`
 *  readback goes to OPBridge, which relays it as `/clock/status.op_quantize`. */
export const OP_QUANTIZE_TARGET = '127.0.0.1:11290';
export const OP_QUANTIZE_GRID_ADDRESS = '/OP-Quantize/set_grid';
export const OP_QUANTIZE_AMOUNT_ADDRESS = '/OP-Quantize/set_amount';
/** The device's own default amount (operator, 2026-10-10). */
export const OP_QUANTIZE_DEFAULT_AMOUNT = 80;
export const ABLETON_LAUNCH_QUANT_ADDRESS = '/OP-BridgeSync/set_launch_quant';
export const ABLETON_METRONOME_ADDRESS = '/OP-BridgeSync/set_metronome';
export const ABLETON_PLAY_ADDRESS = '/OP-BridgeSync/play';
export const ABLETON_STOP_ADDRESS = '/OP-BridgeSync/stop';
export const ABLETON_CONTINUE_ADDRESS = '/OP-BridgeSync/continue';
/** Live's Session Record OFF (`AbletonEndSessionRecordStep`). The NEW prefix: this verb has no old-prefix history
 *  (OPM4LBuilder, 2026-10-16); `OP-BridgeSync v3.26-rc4` and later take it, on the same 11210 as the rest. */
export const ABLETON_END_SESSION_RECORD_ADDRESS = '/OP-BridgeSync/end_session_record';

/** Snap the slave's playhead to the master's position. A DIFFERENT device
 *  and a different prefix from everything above - `OP-AbletonSyncReceive`,
 *  not `OP-AbletonSync`. Shipped device-side in v0.4-rc1; the slave's set
 *  still loads v0.3-rc1 until the operator promotes it, and an unknown
 *  address is a silent no-op at `udpreceive`. */
export const ABLETON_RESYNC_ADDRESS = '/OP-AbletonSyncReceive/resync';

/** Release the sustain pedal on every `OP-SustainStop` track. FROZEN: it
 *  names the device's old identity (ClipSustainFix) on purpose, because
 *  OPBridge's `clip-sustain-kill` action hardcodes the same string. See
 *  `AbletonSustainKillStep`. */
export const ABLETON_SUSTAIN_KILL_ADDRESS = '/OP-ClipSustainFix/panic';

/** `udpreceive 11250` in `OP-SustainStop`, on the Ableton machine's loopback
 *  (read in the Mac's installed `.maxpat` by OPM4LBuilder, 2026-09-23). */
export const SUSTAIN_STOP_TARGET = '127.0.0.1:11250';

/** Device-side clamps, mirrored here so the UI never shows a value the
 *  device silently refused. The device clips rather than rejecting, so an
 *  out-of-range send lands as the clamp bound with no error anywhere. */
const TEMPO_MIN = 20;
const TEMPO_MAX = 999;
const SIG_NUM_MIN = 1;
const SIG_NUM_MAX = 99;
const SIG_DEN_MIN = 1;
const SIG_DEN_MAX = 32;

function clamp(n: number, lo: number, hi: number): number {
  return Math.max(lo, Math.min(hi, n));
}

/**
 * Handle a step aimed at the LOCAL practice clock.
 *
 * Returns a `FireResult` when the step was handled locally, or `null` when
 * the step is not a practice-clock step and should continue to normal
 * bridge dispatch. Called from `fireAction` BEFORE bridge resolution — a
 * practice-clock control must keep working on a rig with no bridge paired,
 * which is the disconnected-rehearsal case it exists for.
 *
 * Everything here is DISPLAY-ONLY per DEC-092: it moves the playhead and
 * the beat dots and changes nothing Live, OPBridge or any MIDI output
 * actually emits.
 */
function applyPracticeClockStep(step: ActionStep): FireResult | null {
  switch (step.type) {
    case 'ableton.tempo':
      if (step.clock !== 'practice') return null;
      setPracticeBpm(clamp(step.bpm, TEMPO_MIN, TEMPO_MAX));
      return { target: '<practice>', kind: 'ok' };

    case 'ableton.timesig':
      if (step.clock !== 'practice') return null;
      setPracticeSig(
        clamp(Math.round(step.numerator), SIG_NUM_MIN, SIG_NUM_MAX),
        clamp(Math.round(step.denominator), SIG_DEN_MIN, SIG_DEN_MAX),
      );
      return { target: '<practice>', kind: 'ok' };

    case 'practice.transport':
      // Its own step kind, never a target toggle on the Ableton one - the
      // verbs are inverted between the two clocks (practice `stop` REWINDS,
      // Ableton `stop` PAUSES), so one control carrying both meanings is a
      // control that gets read wrong on stage. `'continue'` is not
      // expressible here at all; see the schema comment.
      setPracticePlaying(step.action === 'play');
      return { target: '<practice>', kind: 'ok' };

    default:
      return null;
  }
}

/** Land a stored OP-Motion preset: one `/OP-Motion/land` per bus it holds, sent now (the device arrives on the next
 *  downbeat by itself). A missing or empty preset is refused, never sent as nothing. */
async function sendMotionLand(presetId: string, cfg: BridgeConfig, conn: BridgeConnection): Promise<FireResult> {
  const preset = motionPresetById(presetId);
  if (!preset) return { target: cfg.id, kind: 'unsupported-step', detail: 'no landing preset picked (capture one first)' };
  const sends = landSends(preset);
  if (sends.length === 0) return { target: cfg.id, kind: 'unsupported-step', detail: `landing preset "${preset.name}" holds nothing to land` };
  for (const x of sends) {
    const r = await oscOnBridge(x.address, x.args, cfg, conn, MOTION_OSC_TARGET);
    if (r.kind !== 'ok') return r;
  }
  return { target: cfg.id, kind: 'ok' };
}

async function oscOnBridge(
  address: string,
  args: Array<number | string>,
  cfg: BridgeConfig,
  conn: BridgeConnection,
  // Defaults to the master's loopback device, which is where every Ableton
  // step but `ableton.resync` goes. Explicit at the one call site that
  // leaves the box, so a reader of that call sees the machine change.
  oscTarget: string = ABLETON_SYNC_TARGET,
): Promise<FireResult> {
  try {
    await conn.httpClient().oscSend(address, args, oscTarget);
    return { target: cfg.id, kind: 'ok' };
  } catch (err) {
    const msg = err instanceof OpbridgeError ? `${err.kind}: ${err.message}`
              : err instanceof Error           ? err.message
              : String(err);
    return { target: cfg.id, kind: 'http-error', detail: msg };
  }
}

function sendAbletonTempo(
  step: AbletonTempoStep, cfg: BridgeConfig, conn: BridgeConnection,
): Promise<FireResult> {
  return oscOnBridge(ABLETON_TEMPO_ADDRESS, [clamp(step.bpm, TEMPO_MIN, TEMPO_MAX)], cfg, conn);
}

function sendAbletonTimeSig(
  step: AbletonTimeSigStep, cfg: BridgeConfig, conn: BridgeConnection,
): Promise<FireResult> {
  return oscOnBridge(
    ABLETON_TIME_SIG_ADDRESS,
    [
      clamp(Math.round(step.numerator), SIG_NUM_MIN, SIG_NUM_MAX),
      clamp(Math.round(step.denominator), SIG_DEN_MIN, SIG_DEN_MAX),
    ],
    cfg, conn,
  );
}

function sendAbletonTransport(
  step: AbletonTransportStep, cfg: BridgeConfig, conn: BridgeConnection,
): Promise<FireResult> {
  const address = step.action === 'play' ? ABLETON_PLAY_ADDRESS
                : step.action === 'stop' ? ABLETON_STOP_ADDRESS
                : ABLETON_CONTINUE_ADDRESS;
  return oscOnBridge(address, [], cfg, conn);
}

/**
 * Record quantization. Validated through `normalizeRecQuant` — the RECORD
 * vocabulary, not the launch one.
 *
 * A label the device does not recognise is a **silent no-op device-side**:
 * it prints `RX_REC_QUANT_BAD` to the Max Console, which nobody is watching
 * during a show, and OPBridge reports a cheerful 200 for the send. So an
 * invalid label is refused HERE, loudly, rather than dispatched hopefully.
 */
async function sendAbletonRecQuant(
  step: AbletonRecQuantStep, cfg: BridgeConfig, conn: BridgeConnection,
): Promise<FireResult> {
  const label = normalizeRecQuant(step.label);
  if (label === null) {
    return {
      target: cfg.id,
      kind: 'unsupported-step',
      detail: `"${step.label}" is not a record-quantization label (did you mean a LAUNCH quant value? they are different vocabularies)`,
    };
  }
  // In order, and stop at the first failure: on OP-Quantize, Live's own
  // quantize goes to None before the devices take the grid.
  let last: FireResult = { target: cfg.id, kind: 'ok' };
  for (const o of recQuantSends(label, step.via)) {
    last = await oscOnBridge(o.address, o.args, cfg, conn, o.target);
    if (last.kind !== 'ok') {
      return o.address === ABLETON_REC_QUANT_ADDRESS && step.via === 'op-quantize'
        ? { ...last, detail: `could not set Live's own record quantize to None, so OP-Quantize was not sent (${last.detail ?? last.kind})` }
        : last;
    }
  }
  return last;
}

/**
 * The OSC packets one record-quantize choice becomes, in send order. Shared
 * by the tap path above and the timeline push (`bridgeSync.ts`), so the two
 * can never disagree about what an OP-Quantize step does.
 *
 * Live: `set_rec_quant <label>` to OP-AbletonSync.
 * OP-Quantize: `set_rec_quant 0` to OP-AbletonSync FIRST, then `set_grid
 * <label>` to the devices. OP-Quantize only works while Live's own record
 * quantize is None; the device sets None once, at load, and anything that
 * switches it back on afterwards (a Live row, Live's own menu) silently stops
 * OP-Quantize working. Re-sending None on every choice puts it right with
 * the next press. Operator, 2026-10-15: "when OP-Quantize is used, ableton's
 * rec quantize must be set to 0 otherwise it won't work properly".
 */
export function recQuantSends(label: RecQuantLabel, via?: RecQuantVia): Array<{ address: string; args: string[]; target: string }> {
  if (via === 'op-quantize') {
    return [
      { address: ABLETON_REC_QUANT_ADDRESS, args: ['0'], target: ABLETON_SYNC_TARGET },
      { address: OP_QUANTIZE_GRID_ADDRESS, args: [label], target: OP_QUANTIZE_TARGET },
    ];
  }
  return [{ address: ABLETON_REC_QUANT_ADDRESS, args: [label], target: ABLETON_SYNC_TARGET }];
}

/**
 * OP-Quantize's AMOUNT: how far each take's new notes move toward the grid,
 * 0-100 percent (the device clamps too). Its own send rather than a step
 * kind, like transpose: it is a level on one item, not something a button
 * or a timeline step picks. Every instance (one per track) hears it once.
 */
export async function sendOpQuantizeAmount(amount: number, target = ''): Promise<FireResult[]> {
  const targets = resolveTargets(target);
  if (targets.length === 0) {
    return [{ target: target || '<default>', kind: 'no-bridges' }];
  }
  const pct = clamp(Math.round(amount), 0, 100);
  return Promise.all(targets.map(async (cfg): Promise<FireResult> => {
    const conn = getConnection(cfg.id);
    if (!conn) return { target: cfg.id, kind: 'bridge-unknown' };
    if (conn.status() !== 'connected') {
      return { target: cfg.id, kind: 'bridge-disconnected', detail: conn.status() };
    }
    return oscOnBridge(OP_QUANTIZE_AMOUNT_ADDRESS, [pct], cfg, conn, OP_QUANTIZE_TARGET);
  }));
}

/** Launch quantization. Validated through `normalizeLaunchQuant` — a
 *  DIFFERENT vocabulary from record quant despite the shared label text. */
function sendAbletonLaunchQuant(
  step: AbletonLaunchQuantStep, cfg: BridgeConfig, conn: BridgeConnection,
): Promise<FireResult> {
  const label = normalizeLaunchQuant(step.label);
  if (label === null) {
    return Promise.resolve({
      target: cfg.id,
      kind: 'unsupported-step',
      detail: `"${step.label}" is not a launch-quantization label (did you mean a RECORD quant value? they are different vocabularies)`,
    });
  }
  return oscOnBridge(ABLETON_LAUNCH_QUANT_ADDRESS, [label], cfg, conn);
}

/**
 * Live's audible metronome click.
 *
 * `'toggle'` inverts the last state we heard on `/clock/status`. If we have
 * never heard from the device, a toggle has nothing to invert — refused
 * rather than guessed, because guessing "off, so send on" would turn the
 * click ON in a room where the operator had it deliberately off.
 */
function sendAbletonMetronome(
  step: AbletonMetronomeStep, cfg: BridgeConfig, conn: BridgeConnection,
): Promise<FireResult> {
  let on: boolean;
  if (step.mode === 'toggle') {
    const observed = abletonState().metronome;
    if (observed === null) {
      return Promise.resolve({
        target: cfg.id,
        kind: 'unsupported-step',
        detail: 'metronome toggle needs a known state; the device has not reported one yet',
      });
    }
    on = !observed.on;
  } else {
    on = step.mode === 'on';
  }
  return oscOnBridge(ABLETON_METRONOME_ADDRESS, [on ? 1 : 0], cfg, conn);
}

/**
 * Snap the SLAVE Ableton's playhead to the master's position.
 *
 * **Refuses a step that has not named its bridge, and that refusal is the
 * feature.** `step.target === ''` is `fanoutTargets`' sentinel for "follow
 * whichever bridge is default" - and the default bridge is the master, the
 * machine that runs Live, the clock and the MIDI out. Every other Ableton
 * step wants exactly that. This one is definitionally somewhere else.
 *
 * Sending it anyway would put a datagram on the MASTER's 127.0.0.1:11260,
 * where nothing is listening. OPBridge would answer `{ok:true}`, UDP would
 * swallow it, and the operator would be looking at a button that reports
 * success and does nothing - during the one situation the button exists
 * for. Since the address alone can no longer distinguish the two machines
 * (it is loopback on both), the bridge choice is the only thing that can,
 * so it is required rather than assumed.
 *
 * The counter-case, honestly: if the operator ever makes streampc the
 * DEFAULT bridge, this refusal is wrong. That would mean the slave also
 * owns the clock, the mixer and the MIDI out for the master's Live session,
 * which is not a rig that works - so the trade is a refusal that is wrong
 * in a configuration that is already broken, against a silent no-op in the
 * normal one.
 *
 * Beyond that there is no validation and no readback: the device takes no
 * arguments and acks nothing. An unrecognised address is a silent no-op at
 * the slave's `udpreceive` and we have no socket on that machine to see it.
 * Everything this function can honestly check, it checks here; what it
 * cannot, it says out loud rather than implying.
 */
function sendAbletonResync(
  step: AbletonResyncStep, cfg: BridgeConfig, conn: BridgeConnection,
): Promise<FireResult> {
  if (!step.target) {
    return Promise.resolve({
      target: cfg.id,
      kind: 'unsupported-step',
      detail: 'Resync must name the bridge running on the slave - set "Sends to" on this button to that machine, not Default. Default means the master, where this send would vanish silently.',
    });
  }
  return oscOnBridge(ABLETON_RESYNC_ADDRESS, [], cfg, conn, ABLETON_SYNC_RECEIVE_TARGET);
}

/**
 * Release the sustain pedal on every `OP-SustainStop` track, on ONE bridge.
 *
 * Same refusal as {@link sendAbletonResync}, for the same reason: the target
 * is loopback, so the bridge is the only thing that picks the machine, and a
 * send to a machine without the device answers `{ok:true}` and vanishes. An
 * unaimed step is refused with a sentence the operator can act on.
 *
 * No readback: the device acks nothing, so a 200 means OPBridge relayed the
 * datagram, not that a note was released.
 */
function sendAbletonSustainKill(
  step: AbletonSustainKillStep, cfg: BridgeConfig, conn: BridgeConnection,
): Promise<FireResult> {
  if (!step.target) {
    return Promise.resolve({
      target: cfg.id,
      kind: 'unsupported-step',
      detail: 'Sustain kill must name the bridge on the computer running Ableton - set "Sends to" on this button to that machine, not Default, or the send can vanish silently.',
    });
  }
  return oscOnBridge(ABLETON_SUSTAIN_KILL_ADDRESS, [], cfg, conn, SUSTAIN_STOP_TARGET);
}

// ============================================================
// OBS - scene switch via OPBridge's /obs/scene/{name}
// ============================================================

/**
 * Switch OBS's active program scene on ONE bridge.
 *
 * Two refusals before anything goes on the wire, both deliberate:
 *
 * 1. **No scene named.** An empty name would build `POST /obs/scene/`, a
 *    DIFFERENT route that does not exist, so the daemon answers 404 and the
 *    operator reads "OBS is broken" instead of "this button is not set up".
 *
 * 2. **No bridge named.** Identical to `sendAbletonResync`'s refusal and for
 *    the same measured reason. OBS runs on the streaming PC; the default
 *    bridge is the music PC. A send to the wrong machine comes back 503
 *    ("OBS not reachable"), which is indistinguishable from OBS simply being
 *    shut - so a misroute presents as a dead feature and gets debugged at
 *    the wrong end. DEC-201's rule applied to an HTTP verb rather than an
 *    OSC datagram: cross-machine reach is by BRIDGE, and an item that has
 *    not chosen one must refuse.
 *
 * On failure the reason is recorded against the bridge in `obs/scenes.ts` as
 * well as returned, so a button that could not switch shows WHY on its face
 * rather than just failing to light.
 */
async function sendObsScene(
  step: ObsSceneStep, cfg: BridgeConfig, conn: BridgeConnection,
): Promise<FireResult> {
  if (!step.scene) {
    return {
      target: cfg.id,
      kind: 'unsupported-step',
      detail: 'No OBS scene chosen - pick one in the Inspector.',
    };
  }
  if (!step.target) {
    return {
      target: cfg.id,
      kind: 'unsupported-step',
      detail: 'This OBS button has not been told which computer is running OBS - choose one in the Computer row of the Inspector. It is refused rather than sent to the default bridge, because a send to the wrong machine comes back as "OBS not reachable" and looks like OBS was shut.',
    };
  }
  try {
    await conn.httpClient().obsSetScene(step.scene);
    recordObsSceneSwitch(cfg.id, step.scene);
    return { target: cfg.id, kind: 'ok' };
  } catch (err) {
    const status = err instanceof OpbridgeError ? err.httpStatus : undefined;
    const msg = err instanceof OpbridgeError ? `${err.kind}: ${err.message}`
              : err instanceof Error           ? err.message
              : String(err);
    if (status === 401) {
      recordObsFailure(cfg.id, 'unauthorized', 'OPBridge has not granted OBS access to OPButtons.');
    } else if (status === 503) {
      recordObsFailure(cfg.id, 'unavailable', 'OBS is not reachable from this bridge.');
    } else {
      recordObsFailure(cfg.id, 'error', msg);
    }
    return { target: cfg.id, kind: 'http-error', detail: msg };
  }
}

// ============================================================
// Run Program - OPBridge's /program/launch/{id} (DEC-524)
// ============================================================

/** The `detail` a 409 launch carries. Exported so the button can tell
 *  "already running" (not a failure - the thing you wanted is happening)
 *  from a real failure without matching on prose. */
export const PROGRAM_ALREADY_RUNNING = 'Already running on that computer.';

/**
 * Run one registered program on ONE bridge.
 *
 * Refuses before the wire on the same two grounds as `sendObsScene`, and the
 * second matters for the same measured reason: program ids are per-machine,
 * so a send to the default bridge when the operator meant the studio PC
 * answers 404 "no program matching id" - a misroute that reads as "the
 * program was removed".
 *
 * Outcomes, each rendered differently on the face:
 *  - 200: spawned. The ack IS the spawn, so the button lights at once.
 *  - 202: an approve-mode program - nothing runs until the operator OKs it
 *    on that machine. The button says so and follows the request.
 *  - 409: already running. Not a failure of the button; reported as such.
 *  - 401: OPBridge has not granted the scope - recorded against the bridge.
 */
async function sendProgramLaunch(
  step: ProgramLaunchStep, cfg: BridgeConfig, conn: BridgeConnection,
): Promise<FireResult> {
  if (!step.programId) {
    return {
      target: cfg.id,
      kind: 'unsupported-step',
      detail: 'No program chosen - pick one in the Inspector.',
    };
  }
  if (!step.target) {
    return {
      target: cfg.id,
      kind: 'unsupported-step',
      detail: 'This Run Program button has not been told which computer to run it on - choose one under "Sends to" in the Inspector. It is refused rather than sent to the default bridge, because program lists are per computer and a wrong one answers "no such program".',
    };
  }
  try {
    const res = await conn.httpClient().programLaunch(step.programId);
    if (res?.status === 'pending' && res.request_id) {
      recordProgramPending(cfg.id, step.programId);
      void followProgramRequest(cfg.id, conn, step.programId, res.request_id);
      return { target: cfg.id, kind: 'ok', detail: 'Waiting for approval on that computer.' };
    }
    recordProgramRunning(cfg.id, step.programId);
    return { target: cfg.id, kind: 'ok' };
  } catch (err) {
    const status = err instanceof OpbridgeError ? err.httpStatus : undefined;
    const msg = err instanceof OpbridgeError ? `${err.kind}: ${err.message}`
              : err instanceof Error           ? err.message
              : String(err);
    if (status === 409) {
      // Already running: the light is (or is about to be) on, which is the
      // true state. Mark it so the face agrees before the next poll.
      recordProgramRunning(cfg.id, step.programId);
      return { target: cfg.id, kind: 'http-error', detail: PROGRAM_ALREADY_RUNNING };
    }
    if (status === 401) {
      recordProgramsFailure(cfg.id, 'unauthorized', 'OPBridge has not granted OPButtons program access.');
    }
    return { target: cfg.id, kind: 'http-error', detail: msg };
  }
}

/**
 * Follow one pending approve-mode launch until it resolves, so the face can
 * drop "waiting for OK" the moment the operator decides rather than at the
 * 60s TTL. Long-polls in 10s slices (OPBridge's cap). On `approved` the
 * program is running; the poll will confirm it.
 */
async function followProgramRequest(
  bridgeId: string, conn: BridgeConnection, programId: string, requestId: string,
): Promise<void> {
  const deadline = Date.now() + 75_000;
  try {
    while (Date.now() < deadline) {
      const r = await conn.httpClient().programLaunchRequest(programId, requestId, 10_000);
      if (r?.status === 'pending') continue;
      if (r?.status === 'approved') recordProgramRunning(bridgeId, programId);
      return;
    }
  } catch {
    // 404 = the request is gone (expired and swept). Either way it is no
    // longer pending, which is all this loop exists to say.
  } finally {
    clearProgramPending(bridgeId, programId);
  }
}

// ============================================================
// Lighting presets - OPBridge's /lighting/fire + /lighting/stop (DEC-601)
// ============================================================

/** OPBridge's own reason from an error body (`{"error": "..."}`), else the
 *  whole message. A 400 names the bad preset, knob, fixture or field, and
 *  that sentence is the useful thing to put on stage. */
function bridgeReason(err: unknown): string {
  const msg = err instanceof Error ? err.message : String(err);
  const brace = msg.indexOf('{');
  if (brace >= 0) {
    try {
      const body = JSON.parse(msg.slice(brace)) as { error?: unknown };
      if (typeof body.error === 'string' && body.error) return body.error;
    } catch { /* not JSON - fall through */ }
  }
  return msg;
}

/** A lighting failure worded for the operator. Each status has a different
 *  fix, and none of them is in the button. */
function lightingFailure(cfg: BridgeConfig, err: unknown, verb: 'play' | 'stop'): FireResult {
  const status = err instanceof OpbridgeError ? err.httpStatus : undefined;
  const machine = machineNameFromInstanceName(cfg.bridge_instance_name) || cfg.name || 'that computer';
  const detail = status === 401 || status === 403
    ? `OPBridge on ${machine} has not given this device lighting presets. Re-pair this device, or update OPBridge.`
    : status === 404 && verb === 'play'
      ? `OPBridge on ${machine} is too old for lighting presets (needs v1.15.154 or later).`
      : status === 503
        ? `OPBridge on ${machine} has no DMX output, so there are no lights to play on.`
        : bridgeReason(err);
  return { target: cfg.id, kind: 'http-error', detail };
}

/**
 * Play a preset as this button's layer (`POST /lighting/fire`).
 *
 * Refused before the wire when unconfigured or UNAIMED, for Run Program's
 * reason and a sharper one: a preset plays on the lights patched on the
 * bridge it is sent to and is never carried across the mesh, so the default
 * bridge would play it on the wrong lights or on none, and say "ok".
 */
async function sendLightingFire(
  step: LightingFireStep, cfg: BridgeConfig, conn: BridgeConnection,
): Promise<FireResult> {
  if (!step.preset) {
    return { target: cfg.id, kind: 'unsupported-step', detail: 'No preset chosen - pick one in the Inspector.' };
  }
  if (!step.target) {
    return {
      target: cfg.id,
      kind: 'unsupported-step',
      detail: 'This Light Preset button has not been told which computer the lights are on - choose one under "Sends to" in the Inspector. It is refused rather than sent to the default bridge, because a preset only plays on the lights wired to the computer it is sent to.',
    };
  }
  try {
    const res = await conn.httpClient().lightingFire({
      id: step.layerId,
      preset: step.preset,
      ...(step.knobs ? { knobs: step.knobs } : {}),
      ...(step.fixtures ? { fixtures: step.fixtures } : {}),
      ...(step.lengthBeats !== undefined ? { lengthBeats: step.lengthBeats } : {}),
    });
    // Lights someone else holds (OPBridge S297 `parked`): the press played, but those lights show nothing.
    const parked = Array.isArray(res?.parked) ? res.parked.filter((p) => p && typeof p.light === 'string') : [];
    return { target: cfg.id, kind: 'ok', ...(parked.length ? { parked } : {}) };
  } catch (err) {
    return lightingFailure(cfg, err, 'play');
  }
}

/** Stop this button's layer. A 404 means the layer is already gone (a
 *  Blackout, a daemon restart, another press) - that is the state asked
 *  for, so it is reported `ok` with a note, never as a failure. */
async function sendLightingStop(
  step: LightingStopStep, cfg: BridgeConfig, conn: BridgeConnection,
): Promise<FireResult> {
  if (!step.target) {
    return { target: cfg.id, kind: 'unsupported-step', detail: 'Not told which computer the lights are on.' };
  }
  try {
    await conn.httpClient().lightingStop(step.layerId);
    return { target: cfg.id, kind: 'ok' };
  } catch (err) {
    if (err instanceof OpbridgeError && err.httpStatus === 404) {
      return { target: cfg.id, kind: 'ok', detail: 'Already stopped.' };
    }
    return lightingFailure(cfg, err, 'stop');
  }
}

// ============================================================
// OPVisual - OPBridge protocol by default, OSC on request (DEC-215)
// ============================================================

/**
 * Send OPVisual messages through the bridge on the machine OPVisual runs on.
 *
 * ## Two channels, one routing key
 *
 * `via` picks the channel (the item's "Sends via"; absent = OPBridge):
 *
 * - **`'opbridge'` (default, DEC-215)** - `POST /opvisual/trigger` with the
 *   message's `frame`. OPBridge forwards it to the OPVisual attached to ITS
 *   `/ws/consumer`, so this reaches OPVisual only through the bridge OPVisual
 *   is linked to. The answer's `opvisual_connected` is the one signal on this
 *   whole path that can say "nothing heard that": a `false` becomes
 *   `app-not-linked` and the caller marks nothing sent.
 * - **`'osc'`** - `POST /osc/send` of the message's `osc` form to
 *   `127.0.0.1:7011`. `/osc/send` only accepts loopback targets and 7011
 *   exists on every machine, so a send to the wrong bridge is accepted,
 *   reported `{ok:true}` and dropped by UDP with no error anywhere.
 *
 * Either way the routing key is the MACHINE (same rule as OBS and Ableton
 * Device, DEC-205). OPVisual runs on the streaming PC beside OBS (operator,
 * 2026-09-24), not on the Ableton Mac. That is why an UNAIMED item is refused
 * here rather than sent to the default bridge: on the OSC channel there is no
 * failure signal to debug from, and on the OPBridge channel the default bridge
 * would answer "not linked" about a machine the operator never chose.
 *
 * `messages` is sent in order and stops at the first failure, so a partial
 * STOP ALL says how far it got rather than reporting success.
 *
 * `ignorePanicLatch` is for OPVisual's own PANIC only. The OPButtons panic
 * latch suppresses every other send while engaged; a visuals panic is part
 * of the same emergency and must still get out.
 */
export async function sendOpVisual(
  messages: OpvMsg[],
  bridgeIds: string[] | undefined,
  opts: { ignorePanicLatch?: boolean; via?: OpvVia | undefined } = {},
): Promise<FireResult> {
  const aimed = (bridgeIds ?? [])[0];
  if (!aimed) {
    return {
      target: '<unaimed>',
      kind: 'unsupported-step',
      detail: 'Not told which computer runs OPVisual - pick it under "Sends to" in the Inspector. Refused rather than sent to the default bridge, because a send to the wrong machine is accepted and silently lost.',
    };
  }
  if (messages.length === 0) {
    return { target: aimed, kind: 'unsupported-step', detail: 'Nothing to send - the cell or column is out of range.' };
  }
  const cfg = resolveBridges(aimed, { ignorePanicLatch: opts.ignorePanicLatch === true })[0];
  if (!cfg) return { target: aimed, kind: 'no-bridges' };
  const conn = getConnection(cfg.id);
  if (!conn) return { target: cfg.id, kind: 'bridge-unknown' };
  if (conn.status() !== 'connected') {
    return { target: cfg.id, kind: 'bridge-disconnected', detail: conn.status() };
  }
  const via = opts.via ?? DEFAULT_OPV_VIA;
  for (let i = 0; i < messages.length; i++) {
    const m = messages[i]!;
    const r = via === 'osc'
      ? await oscOnBridge(m.osc.address, m.osc.args, cfg, conn, OPVISUAL_OSC_TARGET)
      : await opvisualTriggerOnBridge(m, cfg, conn);
    if (r.kind !== 'ok') {
      return messages.length > 1
        ? { ...r, detail: `${i}/${messages.length} sent - ${r.detail ?? r.kind}` }
        : r;
    }
  }
  return { target: cfg.id, kind: 'ok' };
}

export interface OpvPanicResult extends FireResult {
  /** The machine, for the toast (falls back to the stored id). */
  name: string;
  via: OpvVia;
}

/**
 * The main PANIC's OPVisual leg: OPVisual's own `panic` (empty the program,
 * release a latched blackout, look back to neutral; OPVisual DEC-122) on every
 * machine in `aims` (see `opVisualPanicAims`). Concurrent, never throws, one
 * result per aim. Passes the panic latch, like the other panic legs: the
 * latch's repeat is how a held rig stays dark, and OPVisual debounces repeats
 * within 500 ms.
 */
export async function panicOpVisual(
  aims: ReadonlyArray<{ bridgeId: string; via: OpvVia }>,
): Promise<OpvPanicResult[]> {
  return Promise.all(aims.map(async ({ bridgeId, via }) => {
    const cfg = resolveBridges(bridgeId, { ignorePanicLatch: true })[0];
    const name = (cfg && (machineNameFromInstanceName(cfg.bridge_instance_name) || cfg.name)) || bridgeId;
    try {
      const r = await sendOpVisual([panicMsg()], [bridgeId], { ignorePanicLatch: true, via });
      return { ...r, name, via };
    } catch (err) {
      return { target: bridgeId, kind: 'http-error' as const, detail: err instanceof Error ? err.message : String(err), name, via };
    }
  }));
}

/** One message over OPBridge protocol. Every failure names what to do about
 *  it, because each has a different fix and none of them is in the button. */
async function opvisualTriggerOnBridge(
  m: OpvMsg, cfg: BridgeConfig, conn: BridgeConnection,
): Promise<FireResult> {
  const machine = machineNameFromInstanceName(cfg.bridge_instance_name) || cfg.name || 'that computer';
  try {
    const res = await conn.httpClient().opvisualTrigger(m.frame);
    if (res?.opvisual_connected !== true) {
      return {
        target: cfg.id,
        kind: 'app-not-linked',
        detail: `OPVisual is not linked to OPBridge on ${machine}, so nothing happened. Open OPVisual there and check its bridge status reads LINKED.`,
      };
    }
    return { target: cfg.id, kind: 'ok' };
  } catch (err) {
    const status = err instanceof OpbridgeError ? err.httpStatus : undefined;
    const msg = err instanceof OpbridgeError ? `${err.kind}: ${err.message}`
              : err instanceof Error           ? err.message
              : String(err);
    // 404: an OPBridge older than v1.15.141 has no such route. 401/403: a
    // hand-scoped token minted before bundle v18 lacks the scope (OPBridge
    // S285 named three). Both are fixed outside OPButtons, and OSC still
    // works in the meantime, so say both.
    const detail = status === 404
      ? `OPBridge on ${machine} is too old to control OPVisual (needs v1.15.141 or later). Update it, or choose OSC under "Sends via". (${msg})`
      : status === 401 || status === 403
        ? `OPBridge on ${machine} has not given this device OPVisual control. Re-pair this device, or choose OSC under "Sends via". (${msg})`
        : msg;
    return { target: cfg.id, kind: 'http-error', detail };
  }
}

// ============================================================
// Ableton device on/off - OSC to OP-ButtonsDevice inside Live
// ============================================================

/**
 * Switch one named device on one named track.
 *
 * Three refusals before anything goes on the wire. The first two are the
 * ordinary unconfigured cases; the THIRD is the one worth reading:
 *
 * 1. **No track named.** Nothing to address.
 * 2. **No device named.** The device treats an empty filter as `no_match`
 *    (`resolveDevice` returns early on a zero-length key), so this would be
 *    a datagram that can only ever fail.
 * 3. **No bridge named.** ⚠️ The strongest refusal in this file. Everything
 *    else that refuses an unaimed step does so because the wrong machine
 *    answers WRONGLY - a 503, a silent `udpreceive` no-op. Here the wrong
 *    machine answers *successfully*: `/osc/send` only accepts loopback
 *    targets, `127.0.0.1:11280` exists on every machine, OPBridge returns
 *    `{ok:true}` and UDP drops the datagram with no error anywhere in the
 *    stack. So there is no failure signal to debug from at all, and an
 *    unaimed send is indistinguishable from a working one.
 *
 * **No optimistic mirror is written on success**, unlike `sendObsScene`'s
 * `recordObsSceneSwitch`. That is deliberate and is the lesson from the
 * scene-copy path: the device reads the parameter back after writing it and
 * reports what it READ (`OP-ButtonsDevice.js`, "READ BACK, ALWAYS"), so the
 * truth arrives on its own within a push. Writing an assumed value here
 * would light the button from our intention for the gap in between, and an
 * intention that Live rejected would keep it lit.
 */
async function sendAbletonDeviceOn(
  step: AbletonDeviceStep, cfg: BridgeConfig, conn: BridgeConnection,
): Promise<FireResult> {
  if (!step.trackName) {
    return {
      target: cfg.id,
      kind: 'unsupported-step',
      detail: 'No Ableton track named - set one in the Inspector.',
    };
  }
  if (!step.deviceName) {
    return {
      target: cfg.id,
      kind: 'unsupported-step',
      detail: 'No Ableton device named - set one in the Inspector.',
    };
  }
  if (!step.target) {
    return {
      target: cfg.id,
      kind: 'unsupported-step',
      detail: 'This button has not been told which computer runs Ableton - choose one in the Computer row of the Inspector. It is refused rather than sent to the default bridge: the device address is loopback, so a send to the wrong machine is accepted and silently dropped with no error anywhere.',
    };
  }
  const nth = step.nth !== undefined && step.nth >= 1 ? Math.round(step.nth) : 1;
  return oscOnBridge(
    SET_DEVICE_ON_ADDRESS,
    [step.trackName, step.deviceName, nth, step.on ? 1 : 0],
    cfg,
    conn,
    BUTTONS_DEVICE_TARGET,
  );
}

// ============================================================
// Waves Autotune - plain MIDI CC to the primary bridge's default port
// ============================================================

/**
 * Send one MIDI CC (channel 1-16) to the primary bridge's default MIDI
 * output. Used by the Waves Autotune item's key + scale selectors, which
 * ride raw MIDI (unlike Transpose/Capo, which dispatch OSC). Same
 * primary-only target resolution + never-throws contract as
 * {@link fireAction}; the CC number + value are the caller's job (the key/
 * scale value tables live in `autotune/session.ts`).
 */
export async function sendAutotuneCc(
  channel: number,
  cc: number,
  value: number,
  target = '',
): Promise<FireResult[]> {
  const targets = resolveTargets(target);
  if (targets.length === 0) {
    return [{ target: target || '<default>', kind: 'no-bridges' }];
  }
  const ch = clampNibble((Math.round(channel) - 1) & 0x0f);
  const bytes = [0xb0 | ch, cc & 0x7f, value & 0x7f];
  return Promise.all(targets.map((cfg) => sendAutotuneOnBridge(bytes, cfg)));
}

async function sendAutotuneOnBridge(bytes: number[], cfg: BridgeConfig): Promise<FireResult> {
  const conn = getConnection(cfg.id);
  if (!conn) return { target: cfg.id, kind: 'bridge-unknown' };
  if (conn.status() !== 'connected') {
    return { target: cfg.id, kind: 'bridge-disconnected', detail: conn.status() };
  }
  // Autotune targets Ableton, i.e. the rig's default port — never a
  // per-device override. Passed explicitly now that httpSend takes a port.
  if (!cfg.defaultMidiOutput) {
    return { target: cfg.id, kind: 'port-not-configured' };
  }
  return httpSend(conn, cfg, bytes, cfg.defaultMidiOutput);
}

// ============================================================
// Mixer — OSC to the OP-AbletonMixer M4L hub (control half)
// ============================================================

/** UDP target of the OP-AbletonMixer device's udpreceive (control-in).
 *  Fixed on the OPBridge host; chosen to avoid the family binds
 *  (11200 clock out, 11210 sync in, 11220 transpose in). */
export const MIXER_OSC_TARGET = '127.0.0.1:11230';

/** OSC addresses the OP-AbletonMixer device listens on (OP-AbletonMixer.js).
 *  `setVolume`/`setMute`/`rescan` shipped on v0.1; `setSolo`/`setArm`/`setPan`/
 *  `setSend` shipped on v0.2 (LOM sets + feedback), with OPBridge folding the
 *  matching /solo /arm /pan /send /sendcount feedback into /mixer/status. */
export const MIXER_OSC = {
  setVolume: '/OP-AbletonMixer/set_volume',
  setMute: '/OP-AbletonMixer/set_mute',
  rescan: '/OP-AbletonMixer/rescan',
  // v0.2 stub verbs:
  setSolo: '/OP-AbletonMixer/set_solo',
  setArm: '/OP-AbletonMixer/set_arm',
  setPan: '/OP-AbletonMixer/set_pan',
  setSend: '/OP-AbletonMixer/set_send',
  // Master track (live_set.master_track) — no per-track index, volume + pan only.
  setMasterVolume: '/OP-AbletonMixer/set_master_volume',
  setMasterPan: '/OP-AbletonMixer/set_master_pan',
  // Return tracks (live_set.return_tracks) — vol/pan/mute/solo/sends, no arm.
  setReturnVolume: '/OP-AbletonMixer/set_return_volume',
  setReturnPan: '/OP-AbletonMixer/set_return_pan',
  setReturnMute: '/OP-AbletonMixer/set_return_mute',
  setReturnSolo: '/OP-AbletonMixer/set_return_solo',
  setReturnSend: '/OP-AbletonMixer/set_return_send',
} as const;

/** Session/render index base for return tracks (must match session.ts). A strip
 *  index >= this is return `idx - RETURN_BASE`; routes to the `set_return_*` verbs. */
const RETURN_BASE = 2000;

/**
 * Set one mixer strip's volume (normalized 0..1) on the OP-AbletonMixer hub
 * via OPBridge's `POST /osc/send` on the primary bridge. No MIDI port needed
 * — it's an OSC send, same target-resolution + never-throws contract as
 * {@link sendTranspose}. `idx` is the 0-based track position; the value is
 * clamped 0..1. The `/osc/send` scope is in the `opbuttons-full` bundle.
 */
export async function sendMixerVolume(idx: number, value: number, target = ''): Promise<FireResult[]> {
  const v = clamp01(value);
  // Reserved index -1 = the master track (no per-track index on the wire).
  if (idx < 0) return oscToMixer(MIXER_OSC.setMasterVolume, [v], target);
  if (idx >= RETURN_BASE) return oscToMixer(MIXER_OSC.setReturnVolume, [idx - RETURN_BASE, v], target);
  const i = Math.max(0, Math.round(idx));
  return oscToMixer(MIXER_OSC.setVolume, [i, v], target);
}

/** Set one mixer strip's mute (0|1) on the hub. Index >= RETURN_BASE = a return. */
export async function sendMixerMute(idx: number, muted: boolean, target = ''): Promise<FireResult[]> {
  if (idx >= RETURN_BASE) return oscToMixer(MIXER_OSC.setReturnMute, [idx - RETURN_BASE, muted ? 1 : 0], target);
  const i = Math.max(0, Math.round(idx));
  return oscToMixer(MIXER_OSC.setMute, [i, muted ? 1 : 0], target);
}

/** Ask the hub to re-enumerate the bank (manual re-sync after a Live track
 *  add/remove/reorder — v0.1's substitute for topology tracking). */
export async function sendMixerRescan(target = ''): Promise<FireResult[]> {
  return oscToMixer(MIXER_OSC.rescan, [], target);
}

// ── v0.2 stub senders (solo / arm / pan / sends) ─────────────────────────────
// Same wire spine as set_volume/set_mute (primary-only, never-throws). The
// device v0.1 doesn't act on these yet; they're here so the PWA already speaks
// the full strip when OP-AbletonMixer v0.2 lands. See MIXER_OSC note above.

/** Set one strip's solo (0|1) on the hub. Index >= RETURN_BASE = a return. */
export async function sendMixerSolo(idx: number, solo: boolean, target = ''): Promise<FireResult[]> {
  if (idx >= RETURN_BASE) return oscToMixer(MIXER_OSC.setReturnSolo, [idx - RETURN_BASE, solo ? 1 : 0], target);
  const i = Math.max(0, Math.round(idx));
  return oscToMixer(MIXER_OSC.setSolo, [i, solo ? 1 : 0], target);
}

/** Set one strip's record-arm (0|1) on the hub. Stub. */
export async function sendMixerArm(idx: number, arm: boolean, target = ''): Promise<FireResult[]> {
  const i = Math.max(0, Math.round(idx));
  return oscToMixer(MIXER_OSC.setArm, [i, arm ? 1 : 0], target);
}

/** Set one strip's pan (-1..1, 0 = center) on the hub. Index -1 = master. */
export async function sendMixerPan(idx: number, pan: number, target = ''): Promise<FireResult[]> {
  const p = clampBipolar(pan);
  if (idx < 0) return oscToMixer(MIXER_OSC.setMasterPan, [p], target);
  if (idx >= RETURN_BASE) return oscToMixer(MIXER_OSC.setReturnPan, [idx - RETURN_BASE, p], target);
  const i = Math.max(0, Math.round(idx));
  return oscToMixer(MIXER_OSC.setPan, [i, p], target);
}

/** Set one send slot's level (0..1) for a strip on the hub. `sendIdx` = send
 *  slot (A=0, B=1, ...). Stub. */
export async function sendMixerSend(idx: number, sendIdx: number, value: number, target = ''): Promise<FireResult[]> {
  const s = Math.max(0, Math.round(sendIdx));
  const v = clamp01(value);
  if (idx >= RETURN_BASE) return oscToMixer(MIXER_OSC.setReturnSend, [idx - RETURN_BASE, s, v], target);
  const i = Math.max(0, Math.round(idx));
  return oscToMixer(MIXER_OSC.setSend, [i, s, v], target);
}

async function oscToMixer(address: string, args: number[], target: string): Promise<FireResult[]> {
  const targets = resolveTargets(target);
  if (targets.length === 0) {
    return [{ target: target || '<default>', kind: 'no-bridges' }];
  }
  return Promise.all(targets.map((cfg) => oscToMixerOnBridge(address, args, cfg)));
}

async function oscToMixerOnBridge(address: string, args: number[], cfg: BridgeConfig): Promise<FireResult> {
  const conn = getConnection(cfg.id);
  if (!conn) return { target: cfg.id, kind: 'bridge-unknown' };
  if (conn.status() !== 'connected') {
    return { target: cfg.id, kind: 'bridge-disconnected', detail: conn.status() };
  }
  try {
    await conn.httpClient().oscSend(address, args, MIXER_OSC_TARGET);
    return { target: cfg.id, kind: 'ok' };
  } catch (err) {
    const msg = err instanceof OpbridgeError ? `${err.kind}: ${err.message}`
              : err instanceof Error           ? err.message
              : String(err);
    return { target: cfg.id, kind: 'http-error', detail: msg };
  }
}

function clamp01(n: number): number {
  if (!Number.isFinite(n)) return 0;
  return Math.max(0, Math.min(1, n));
}

function clampBipolar(n: number): number {
  if (!Number.isFinite(n)) return 0;
  return Math.max(-1, Math.min(1, n));
}

// ============================================================
// Panic (SD-6) — MIDI all-notes/sound-off + stop armed engines
// ============================================================

/** The three "panic" controller messages, in firing order: CC 120 = All
 *  Sound Off, CC 121 = Reset All Controllers, CC 123 = All Notes Off.
 *  Matches OPBridge's own admin `/midi/panic` primitive (STAGE-DESIGN SD-6). */
export const PANIC_CCS = [120, 121, 123] as const;

const CONSUMER = 'opbuttons';

export interface MidiPanicResult {
  target: string;
  /** The bridge's OPERATOR-FACING name ("Dev PC", "Mac"), when the target
   *  resolved to a real row. A failure message built from `target` alone reads
   *  `br_smoke_second: send failed`, which tells the operator nothing they can
   *  act on mid-set - the entire purpose of naming the failing machine is so
   *  they walk to the right one. Absent only when no row resolved at all. */
  name?: string;
  kind: FireResultKind;
  /** MIDI output port the CC storm was fired to (present on ok / http-error). */
  port?: string;
  /** Count of CC messages OPBridge accepted (best-effort; max 48). */
  sent?: number;
  detail?: string;
}

/** Outcome of the v1 native panic pair (`POST /midi/panic` +
 *  `POST /engines/{consumer}/stopAll`). Both are best-effort companions to
 *  the v0 path, never replacements — see `panicNative`. */
export interface NativePanicResult {
  /** Which bridge this result came from, so a partial panic can name the
   *  machine that did not answer rather than reporting one blended number. */
  target: string;
  /** Output ports OPBridge's global panic reached, or null if it failed.
   *  NOTE: "global" here means every port on THIS ONE machine, never every
   *  machine - the naming misled a reader for months (audit 2026-09-03). */
  portsHit: number | null;
  /** Engines stopAll killed across all families, or null if it failed. */
  enginesStopped: number | null;
  /** Per-leg failure strings, for diagnosis. Empty on a clean run. */
  errors: string[];
}

/**
 * v1 panic (SD-6 §6.2) — fire OPBridge's own data-plane panic endpoints.
 *
 * Deliberately ADDITIVE to the v0 CC storm rather than a replacement, and
 * both run concurrently. Two independent reasons the v0 path must stay:
 *
 *  1. **Reach.** v0 can only hit `defaultMidiOutput`; `/midi/panic` hits
 *     every output port OPBridge owns (4 on the dev PC). Neither is a
 *     superset in every failure mode — if the bearer lacks the panic scope
 *     the native leg 401s while the v0 CC storm still lands, and if the
 *     default port is missing v0 fails while native still clears the rest.
 *  2. **Age.** A bridge older than the Session-138 batch 404s these. Panic
 *     is the one control that must never depend on the peer being current.
 *
 * Never throws: every failure becomes an entry in `errors`, because a
 * partially-failed panic must still report what DID land.
 */
export async function panicNative(target = 'all'): Promise<NativePanicResult[]> {
  const targets = resolveTargets(target, true);
  if (targets.length === 0) {
    return [{ target: target || '<default>', portsHit: null, enginesStopped: null, errors: ['no bridge'] }];
  }
  return Promise.all(targets.map((cfg) => panicNativeOnBridge(cfg)));
}

async function panicNativeOnBridge(cfg: BridgeConfig): Promise<NativePanicResult> {
  const errors: string[] = [];
  const conn = getConnection(cfg.id);
  if (!conn || conn.status() !== 'connected') {
    return { target: cfg.id, portsHit: null, enginesStopped: null, errors: ['bridge disconnected'] };
  }
  const client = conn.httpClient();

  const [panicRes, stopRes] = await Promise.allSettled([
    client.midiPanic(),
    client.stopAllEnginesNative(CONSUMER),
  ]);

  let portsHit: number | null = null;
  if (panicRes.status === 'fulfilled') {
    portsHit = panicRes.value.ports_hit;
    // OPBridge reports per-port failures inside a 200 — surface them rather
    // than reading `ok` alone, or a panic that missed 3 of 4 ports reads clean.
    if (panicRes.value.errors.length > 0) {
      errors.push(...panicRes.value.errors.map((e) => `panic port: ${e}`));
    }
  } else {
    errors.push(engineErr('POST /midi/panic', panicRes.reason));
  }

  let enginesStopped: number | null = null;
  if (stopRes.status === 'fulfilled') {
    enginesStopped = stopRes.value.total;
  } else {
    errors.push(engineErr('POST /engines/stopAll', stopRes.reason));
  }

  return { target: cfg.id, portsHit, enginesStopped, errors };
}

/**
 * Fire the MIDI panic storm — CC 120/121/123 on all 16 channels — to EVERY
 * paired bridge, each on its OWN `defaultMidiOutput` (SD-6 v0). Never throws;
 * degrades to a typed result per bridge when there's no bridge / no port /
 * that bridge is down.
 *
 * DEFAULTS TO `'all'` SINCE 2026-09-03. It defaulted to `''`, which
 * `resolveBridges` resolves to the single default bridge, so PANIC silenced
 * one machine of two and the toast still read `ok`. Both callers (the PANIC
 * button and TimelineLauncher's automatic stuck-note clear on an external
 * Ableton stop) want every bridge, so the default is the fix rather than a
 * per-caller argument.
 *
 * The 48 messages go out as 48 separate well-formed `/midi/send` calls (not
 * one concatenated multi-message byte array) so panic never depends on
 * OPBridge parsing a raw MIDI stream — every message is unambiguously a
 * single valid CC. Fired concurrently; the count of accepted sends is
 * reported so the toast can say exactly what landed.
 */
export async function sendMidiPanic(target = 'all'): Promise<MidiPanicResult[]> {
  const targets = resolveTargets(target, true);
  if (targets.length === 0) {
    return [{ target: target || '<default>', kind: 'no-bridges' }];
  }
  return Promise.all(targets.map((cfg) => panicOnBridge(cfg)));
}

async function panicOnBridge(cfg: BridgeConfig): Promise<MidiPanicResult> {
  const name = cfg.name;
  const conn = getConnection(cfg.id);
  if (!conn) return { target: cfg.id, name, kind: 'bridge-unknown' };
  if (conn.status() !== 'connected') {
    return { target: cfg.id, name, kind: 'bridge-disconnected', detail: conn.status() };
  }
  const port = cfg.defaultMidiOutput;
  if (!port) return { target: cfg.id, name, kind: 'port-not-configured' };

  const client = conn.httpClient();
  const sends: Promise<boolean>[] = [];
  for (let ch = 0; ch < 16; ch++) {
    const status = 0xb0 | ch;
    for (const cc of PANIC_CCS) {
      sends.push(
        client.sendMidi(port, [status, cc, 0]).then(() => true).catch(() => false),
      );
    }
  }
  const results = await Promise.all(sends);
  const sent = results.filter(Boolean).length;
  if (sent === 0) {
    return { target: cfg.id, name, kind: 'http-error', port, sent, detail: `0/${results.length} panic messages sent` };
  }
  return { target: cfg.id, name, kind: 'ok', port, sent };
}

export interface StopEnginesResult {
  target: string;
  /** LFO ids whose stop request returned ok. */
  stopped: string[];
  /** Human-readable reasons when the snapshot / a stop failed. */
  errors: string[];
}

/**
 * Stop every locally-known running/armed engine on EVERY paired bridge
 * (SD-6 v0). Fetches each bridge's engines snapshot then stops each LFO;
 * sequencers + macros join as they get wired. Never throws — engine failures
 * are reported, not raised, so a MIDI panic still counts as sent when engines
 * are unreachable (401 during the migration window, 503 when the service is
 * down, etc.).
 *
 * DEFAULTS TO `'all'` SINCE 2026-09-03. It used to take `resolveTargets(target)[0]`,
 * i.e. the default bridge alone, so an LFO running on the operator's second
 * machine survived PANIC and immediately wrote over the CC 120/121/123 storm.
 * Measured on the real rig by `panic_reaches_every_bridge.ts`: 0 of 48 panic
 * messages reached the second bridge. See audits report 2026-09-03.
 */
export async function stopAllEngines(target = 'all'): Promise<StopEnginesResult[]> {
  const targets = resolveTargets(target, true);
  if (targets.length === 0) {
    return [{ target: target || '<default>', stopped: [], errors: ['no bridge'] }];
  }
  return Promise.all(targets.map((cfg) => stopEnginesOnBridge(cfg)));
}

async function stopEnginesOnBridge(cfg: BridgeConfig): Promise<StopEnginesResult> {
  const conn = getConnection(cfg.id);
  if (!conn || conn.status() !== 'connected') {
    return { target: cfg.id, stopped: [], errors: ['bridge disconnected'] };
  }
  const client = conn.httpClient();
  let lfoIds: string[];
  try {
    const snap = await client.enginesState(CONSUMER);
    lfoIds = snap.lfos.map((l) => l.id);
  } catch {
    // Can't enumerate engines (401 while the engines scope isn't wired yet,
    // 503 service down, network). Benign for panic — we can't stop what we
    // can't see, and in the common case nothing is running. Deliberately NOT
    // a panic-degrading error, so a healthy MIDI panic doesn't show amber
    // just because the migration-window engines query 401'd.
    return { target: cfg.id, stopped: [], errors: [] };
  }
  const stopped: string[] = [];
  const errors: string[] = [];
  await Promise.all(lfoIds.map(async (id) => {
    try {
      await client.stopLfo(CONSUMER, id);
      stopped.push(id);
    } catch (err) {
      errors.push(engineErr(`stop ${id}`, err));
    }
  }));
  return { target: cfg.id, stopped, errors };
}

function engineErr(what: string, err: unknown): string {
  const msg = err instanceof OpbridgeError ? `${err.kind}: ${err.message}`
            : err instanceof Error           ? err.message
            : String(err);
  return `${what}: ${msg}`;
}

// ============================================================
// Target resolution
// ============================================================

function resolveTargets(target: string, ignorePanicLatch = false): BridgeConfig[] {
  // Per-item bridge affinity (supersedes DEC-063's primary-only dispatch,
  // see DECISIONS.md) — every mesh bridge is directly and equally
  // reachable, so an item's own `bridgeId` (passed in as `target`) picks
  // its destination. Delegates to opbridge/store.ts's resolveBridges() so
  // dispatch and per-item liveness (bridgeConnectedReactive) can never
  // disagree about the same item. See that function's doc comment for the
  // exact resolution rules (explicit id / "all" fan-out / empty→default).
  //
  // `ignorePanicLatch` is passed ONLY by the three panic legs. While the latch
  // is engaged resolveBridges() returns zero targets for everything else, which
  // is the suppression - but panic itself must still reach every bridge, since
  // re-firing is half of what the latch is for.
  return resolveBridges(target, { ignorePanicLatch });
}

function isConnected(bridgeId: string): boolean {
  const conn = getConnection(bridgeId);
  return conn?.status() === 'connected';
}

// ============================================================
// Per-bridge dispatch
// ============================================================

async function fireOnBridge(step: ActionStep, cfg: BridgeConfig): Promise<FireResult> {
  const conn = getConnection(cfg.id);
  if (!conn) return { target: cfg.id, kind: 'bridge-unknown' };
  if (conn.status() !== 'connected') {
    return { target: cfg.id, kind: 'bridge-disconnected', detail: conn.status() };
  }

  switch (step.type) {
    case 'midi.cc':   return sendMidiCc(step, cfg, conn);
    case 'midi.note': return sendMidiNote(step, cfg, conn);
    case 'midi.pc':   return sendMidiPc(step, cfg, conn);
    case 'midi.raw':  return sendMidiRaw(step, cfg, conn);
    // Ableton Control -> OP-AbletonSync on 11210. Practice-clock variants of
    // the first three never reach here; `fireAction` intercepts them before
    // bridge resolution (see applyPracticeClockStep).
    case 'ableton.tempo':        return sendAbletonTempo(step, cfg, conn);
    case 'ableton.timesig':      return sendAbletonTimeSig(step, cfg, conn);
    case 'ableton.transport':    return sendAbletonTransport(step, cfg, conn);
    // 'practice.transport' never reaches here - fireAction intercepts it
    // before bridge resolution, same as the practice-targeted tempo/timesig
    // steps. It falls to the default below only if that intercept breaks,
    // which is the loud failure we want rather than a silent no-op.
    case 'ableton.recquant':     return sendAbletonRecQuant(step, cfg, conn);
    case 'ableton.launchquant':  return sendAbletonLaunchQuant(step, cfg, conn);
    case 'ableton.metronome':    return sendAbletonMetronome(step, cfg, conn);
    // OP-Motion on Main holds Live's crossfader: its own port (11320), on the default bridge like the metronome.
    case 'opmotion.crossfade': { const c = liveClockReport(); const x = crossfadeSend(step, liveBeatsPerBar(c?.sigNum, c?.sigDen)); return oscOnBridge(x.address, x.args, cfg, conn, MOTION_OSC_TARGET); }
    // OP-Motion landing presets (2026-10-24): a capture is answered through OPBridge's rule, a land goes out per bus.
    case 'opmotion.capture':     return startMotionCapture({ bus: step.bus, into: step.into }, (args) => oscOnBridge(MOTION_CAPTURE_ADDRESS, args, cfg, conn, MOTION_OSC_TARGET));
    case 'opmotion.land':        return sendMotionLand(step.presetId, cfg, conn);
    // Always off, no args; on the default bridge with the rest of OP-BridgeSync.
    case 'ableton.endsessionrecord': return oscOnBridge(ABLETON_END_SESSION_RECORD_ADDRESS, [], cfg, conn);
    // The one Ableton step that LEAVES THE BOX: it targets the slave's
    // OP-AbletonSyncReceive over the LAN, not OP-AbletonSync on loopback
    // like every case above it.
    case 'ableton.resync':       return sendAbletonResync(step, cfg, conn);
    // Loopback on the aimed bridge, a different port (11250) from
    // OP-AbletonSync; refused when unaimed (see sendAbletonSustainKill).
    case 'ableton.sustainkill':  return sendAbletonSustainKill(step, cfg, conn);
    // OBS scene switch. Like resync it LEAVES the music PC: OBS runs on
    // the streaming machine, so the bridge is the routing key and an
    // unset one is refused rather than sent (see sendObsScene).
    case 'obs.scene':            return sendObsScene(step, cfg, conn);
    case 'abletonDevice.setOn':  return sendAbletonDeviceOn(step, cfg, conn);
    // Run Program: an HTTP verb on a NAMED bridge, like OBS; an unaimed step
    // is refused rather than sent (see sendProgramLaunch).
    case 'program.launch':       return sendProgramLaunch(step, cfg, conn);
    // Lighting preset: an HTTP verb on a NAMED bridge, the one the lights
    // are wired to; an unaimed step is refused (see sendLightingFire).
    case 'lighting.fire':        return sendLightingFire(step, cfg, conn);
    case 'lighting.stop':        return sendLightingStop(step, cfg, conn);
    default:
      // OSC / Wait / HTTP / Stream Deck / engine arms / panic are out of
      // scope this commit. Returning a typed result keeps callers from
      // silently swallowing an unhandled step.
      return {
        target: cfg.id,
        kind: 'unsupported-step',
        detail: `step type "${(step as ActionStep).type}" not yet wired in fireAction`,
      };
  }
}

// ============================================================
// MIDI builders
// ============================================================

/** Which MIDI output a step goes to. `step.port` wins when present; absent
 *  falls back to the bridge's `defaultMidiOutput` (DEC-108's canonical
 *  `"OPButtons"` port), which is what all but device-targeting items use.
 *
 *  Returning `null` means neither exists — the caller turns that into
 *  `port-not-configured`. Note an explicit `step.port` makes the step
 *  sendable even on a bridge with no default configured, which is correct:
 *  the step named its own destination. */
function resolvePort(step: MidiStepBase, cfg: BridgeConfig): string | null {
  return step.port ?? cfg.defaultMidiOutput ?? null;
}

async function sendMidiCc(
  step: MidiCcStep,
  cfg: BridgeConfig,
  conn: BridgeConnection,
): Promise<FireResult> {
  const port = resolvePort(step, cfg);
  if (!port) return { target: cfg.id, kind: 'port-not-configured' };
  // MIDI CC status nibble = 0xB0. Channel is 1-16 in schema, 0-15 on wire.
  const channel = clampNibble((step.channel - 1) & 0x0f);
  const status = 0xb0 | channel;
  const value = step.value & 0x7f;
  const bytes = [status, step.cc & 0x7f, value];
  const result = await httpSend(conn, cfg, bytes, port);
  // Rig State send-cache write (Milestone 2 foundation, 2026-08-15) - only
  // on a confirmed send, never on a failed/unreachable one, so "capture"
  // never claims a value reached the rig when it didn't.
  // Not on a capture bridge (mod sharing asks "what WOULD this send"):
  // recording it would claim a value reached the rig when nothing did.
  if (result.kind === 'ok' && !isCaptureBridge(cfg.id)) {
    // Keyed portless when the bridge's default carried it (DEFAULT_PORT_TOKEN).
    writeSendCacheRaw(midiCcTargetId(step.port ?? DEFAULT_PORT_TOKEN, step.channel, step.cc), value);
  }
  return result;
}

async function sendMidiNote(
  step: MidiNoteStep,
  cfg: BridgeConfig,
  conn: BridgeConnection,
): Promise<FireResult> {
  const port = resolvePort(step, cfg);
  if (!port) return { target: cfg.id, kind: 'port-not-configured' };
  const noteNum = resolveNote(step.note);
  if (noteNum === null) {
    return { target: cfg.id, kind: 'bad-note', detail: String(step.note) };
  }
  const channel = clampNibble((step.channel - 1) & 0x0f);
  const noteOnStatus = 0x90 | channel;
  const noteOffStatus = 0x80 | channel;
  const velocity = step.velocity & 0x7f;
  const durationMs = resolveDurationMs(step.duration);
  // Fire note-on, schedule note-off. We don't await the note-off — the
  // schedule is fire-and-forget; an offline bridge during the off window
  // is logged but doesn't fail the original action. The note-off MUST go to
  // the same resolved port as the note-on, or a stuck note is unkillable.
  const onResult = await httpSend(conn, cfg, [noteOnStatus, noteNum, velocity], port);
  const noteTargetId = midiNoteTargetId(step.port ?? DEFAULT_PORT_TOKEN, step.channel, noteNum);
  if (onResult.kind === 'ok' && !isCaptureBridge(cfg.id)) {
    writeSendCacheRaw(noteTargetId, velocity);
    if (durationMs > 0) {
      setTimeout(() => {
        void httpSend(conn, cfg, [noteOffStatus, noteNum, 0], port).then((offResult) => {
          if (offResult.kind === 'ok') writeSendCacheRaw(noteTargetId, 0);
        }).catch(() => {
          /* note-off is best-effort */
        });
      }, durationMs);
    }
  }
  return onResult;
}

async function sendMidiPc(
  step: MidiProgramChangeStep,
  cfg: BridgeConfig,
  conn: BridgeConnection,
): Promise<FireResult> {
  const port = resolvePort(step, cfg);
  if (!port) return { target: cfg.id, kind: 'port-not-configured' };
  // Program Change status nibble = 0xC0, and PC is a TWO-byte message — there
  // is no data2. OPBridge's `resolve_bytes` builds a 2-byte message when
  // `data2` is omitted, and passing raw `bytes` here sidesteps the question
  // entirely. Channel is 1-16 in schema, 0-15 on wire (same as CC/note).
  const channel = clampNibble((step.channel - 1) & 0x0f);
  const status = 0xc0 | channel;
  return httpSend(conn, cfg, [status, step.program & 0x7f], port);
}

async function sendMidiRaw(
  step: MidiRawStep,
  cfg: BridgeConfig,
  conn: BridgeConnection,
): Promise<FireResult> {
  const port = resolvePort(step, cfg);
  if (!port) return { target: cfg.id, kind: 'port-not-configured' };
  return httpSend(conn, cfg, step.bytes, port);
}

// ============================================================
// HTTP
// ============================================================

async function httpSend(
  conn: BridgeConnection,
  cfg: BridgeConfig,
  bytes: number[],
  port: string,
): Promise<FireResult> {
  try {
    await conn.httpClient().sendMidi(port, bytes);
    return { target: cfg.id, kind: 'ok' };
  } catch (err) {
    // 422 = OPBridge couldn't open this named port. Per OPBridge's own
    // guidance (S175): call /midi/rescan BEFORE concluding the port is dead
    // - a stale cached handle self-heals here - then retry once. Escalate
    // straight to restart_if_unrecoverable=true rather than a bare rescan:
    // MidiService::send already re-enumerates on every open, so a bare
    // rescan has nothing to drop and cannot fix a wedged WinMM session; the
    // restart is the only cure OPBridge has for that class, and its on-disk
    // cooldown makes a retry loop here safe. Only one retry - a second 422
    // means the port genuinely doesn't exist, which is a real error.
    if (err instanceof OpbridgeError && err.httpStatus === 422) {
      try {
        await conn.httpClient().midiRescan(true);
        await conn.httpClient().sendMidi(port, bytes);
        return { target: cfg.id, kind: 'ok' };
      } catch (retryErr) {
        const msg = retryErr instanceof OpbridgeError ? `${retryErr.kind}: ${retryErr.message}`
                  : retryErr instanceof Error           ? retryErr.message
                  : String(retryErr);
        return { target: cfg.id, kind: 'http-error', detail: msg };
      }
    }
    const msg = err instanceof OpbridgeError ? `${err.kind}: ${err.message}`
              : err instanceof Error           ? err.message
              : String(err);
    return { target: cfg.id, kind: 'http-error', detail: msg };
  }
}

// ============================================================
// Helpers
// ============================================================

function clampNibble(n: number): number {
  return Math.max(0, Math.min(15, n));
}

/** Accepts a MIDI note number (0-127) or a note name like "C4" / "F#-1".
 *  Roland convention: C-2 = 0. Returns null on parse failure. */
function resolveNote(input: number | string): number | null {
  if (typeof input === 'number') {
    if (!Number.isFinite(input)) return null;
    const rounded = Math.round(input);
    if (rounded < 0 || rounded > 127) return null;
    return rounded;
  }
  const cleaned = input.replace(/=/g, '-').trim();
  const m = cleaned.match(/^([A-G][#b]?)(-?\d+)$/);
  if (!m) return null;
  const [, letter, octStr] = m;
  const semis: Record<string, number> = {
    C: 0, 'C#': 1, Db: 1, D: 2, 'D#': 3, Eb: 3, E: 4, F: 5,
    'F#': 6, Gb: 6, G: 7, 'G#': 8, Ab: 8, A: 9, 'A#': 10, Bb: 10, B: 11,
  };
  const s = semis[letter!];
  if (s == null) return null;
  const oct = parseInt(octStr!, 10);
  const n = (oct + 2) * 12 + s;
  if (n < 0 || n > 127) return null;
  return n;
}

/** Schema permits `duration: number | string`. Numbers are ms today.
 *  Beat-relative strings ("1b", "0.5b") need the active tempo; that
 *  routing isn't wired here yet — treat as 0 (instant off skipped). */
function resolveDurationMs(input: number | string): number {
  if (typeof input === 'number') return Math.max(0, input);
  // "1b" / "0.5b" → fall through. Tempo-aware resolution lives in the
  // legacy bridge path today; once the OPBridge transport broadcast is
  // wired into PWA state, this will resolve against that.
  return 0;
}

// ============================================================
// Lighting - HTTP to OPBridge's DMX drive routes
// ============================================================

/**
 * WHY DMX GOES TO EXACTLY ONE BRIDGE, and never fans out like MIDI.
 *
 * Every other send in this file fans out across an item's selected bridges,
 * because two bridges with two MIDI ports are two destinations. DMX is not
 * shaped that way. A fixture records which mesh node physically drives it
 * (`outputNodeId`, DEC-471), OPBridge ROUTES a drive command to that owner,
 * and a node that does not own a fixture DROPS the command rather than
 * relaying it - permanently, by design, so a modulator on one node can never
 * drive a light on another. So one reachable bridge reaches the whole rig,
 * and fanning out would send every fixture's command to every node for all
 * but one of them to discard.
 *
 * `resolveTargets` still runs, so an item's "Sends to" is honoured for WHICH
 * bridge is asked; only the first is used. An operator who set an item to
 * "All" gets the first, not a duplicate storm.
 */
function firstDmxBridge(target: string): BridgeConfig | null {
  const targets = resolveTargets(target);
  // 'all' lists every row, offline ones included: prefer one that answers
  // (2026-11-03 audit A-9).
  return targets.find((cfg) => getConnection(cfg.id)?.status() === 'connected') ?? targets[0] ?? null;
}

/** Where ONE light's write goes (audit 2026-11-03 A-9, the rest of it). A
 *  control with no "Sends to" goes to the default computer, as always - but
 *  with the default down and the light output by another computer that is up
 *  (STREAMPC owns the DMX), it goes to that one instead: each fixture records
 *  the node that outputs it (`outputNodeId`, DEC-471). Before, every light
 *  control went dead with the default even though the lights were fine. An
 *  explicit "Sends to" is never second-guessed. */
function dmxBridgeFor(fixtureId: string, target: string, chosen: BridgeConfig): BridgeConfig {
  if (target !== '' || getConnection(chosen.id)?.status() === 'connected') return chosen;
  const owner = fixtures().find((f) => f.id === fixtureId)?.outputNodeId;
  const row = owner ? bridges().find((b) => b.nodeId === owner) : undefined;
  return row && getConnection(row.id)?.status() === 'connected' ? row : chosen;
}

/** Whether a light control can reach its lights (A-9): the default is up, or
 *  every light it aims at is output by a computer that is up. Reactive. */
export function dmxReachableReactive(fixtureIds: readonly string[]): boolean {
  if (anyBridgeConnectedReactive(undefined)) return true;
  if (fixtureIds.length === 0) return false;
  const rig = fixtures();
  return fixtureIds.every((id) => {
    const owner = rig.find((f) => f.id === id)?.outputNodeId;
    const row = owner ? bridges().find((b) => b.nodeId === owner) : undefined;
    return !!row && bridgeConnectedReactive(row.id);
  });
}

/** One fixture's worth of DMX outcome. Deliberately per-fixture rather than
 *  per-request: a group write is N requests and "4 of 5 went" is the only
 *  honest thing to say about it. */
export interface DmxFireResult extends FireResult {
  fixtureId: string;
}

async function dmxOnFixture(
  cfg: BridgeConfig,
  fixtureId: string,
  run: (conn: BridgeConnection) => Promise<unknown>,
): Promise<DmxFireResult> {
  const conn = getConnection(cfg.id);
  if (!conn) return { target: cfg.id, fixtureId, kind: 'bridge-unknown' };
  if (conn.status() !== 'connected') {
    return { target: cfg.id, fixtureId, kind: 'bridge-disconnected', detail: conn.status() };
  }
  try {
    await run(conn);
    return { target: cfg.id, fixtureId, kind: 'ok' };
  } catch (err) {
    if (err instanceof OpbridgeError) {
      return { target: cfg.id, fixtureId, kind: 'http-error', detail: `${err.kind}: ${err.message}` };
    }
    return { target: cfg.id, fixtureId, kind: 'http-error', detail: (err as Error).message };
  }
}

/**
 * Set colour / level / power on a list of fixtures.
 *
 * Goes through `/state`, the PARTIAL write, so an omitted field is left
 * alone. That is what lets a level control move a level without telling the
 * fixture a colour it was never asked about - see `dmxSetState`'s doc comment
 * for why `/color` would have forced exactly that.
 *
 * Never throws. Every failure lands as a typed per-fixture result, same
 * contract as `fireAction`, because a lighting control that throws inside a
 * pointer handler is a control that fails silently on stage.
 */
export async function sendDmxState(
  fixtureIds: string[],
  body: {
    rgb?: { r: number; g: number; b: number };
    level?: number;
    on?: boolean;
    subfixture?: number;
    preset?: string;
  },
  target = '',
): Promise<DmxFireResult[]> {
  if (fixtureIds.length === 0) return [];
  const cfg = firstDmxBridge(target);
  if (!cfg) {
    return fixtureIds.map((fixtureId) => ({ target: target || '<default>', fixtureId, kind: 'no-bridges' as const }));
  }
  return Promise.all(
    fixtureIds.map((id) => dmxOnFixture(dmxBridgeFor(id, target, cfg), id, (conn) => conn.httpClient().dmxSetState(id, body))),
  );
}

/** Arm (or, with an empty `effect`, STOP) a named effect on a list of
 *  fixtures. The empty-means-stop convention is OPBridge's, not ours. */
export async function sendDmxEffect(
  fixtureIds: string[],
  body: {
    effect: string;
    brightness?: number;
    color?: { r: number; g: number; b: number };
    subfixture?: number;
  },
  target = '',
): Promise<DmxFireResult[]> {
  if (fixtureIds.length === 0) return [];
  const cfg = firstDmxBridge(target);
  if (!cfg) {
    return fixtureIds.map((fixtureId) => ({ target: target || '<default>', fixtureId, kind: 'no-bridges' as const }));
  }
  // POWER FIRST, and this is measured rather than defensive. An effect on a
  // fixture resting at `on:false` renders nothing: 18 samples of a running
  // strobe gave 6/18 lit with the fixture powered on and 0/18 with it off,
  // the ON arm being the positive control that rules out "the strobe never
  // ran". `/effect` has no power field, so the only way to arm an effect that
  // is actually visible is a `/state` write in front of it.
  //
  // NOT done when STOPPING (an empty effect): "stop the effects" must never
  // be the thing that turns a light on.
  if (body.effect.trim() !== '') {
    await Promise.all(fixtureIds.map((id) => dmxOnFixture(
      dmxBridgeFor(id, target, cfg), id,
      (conn) => conn.httpClient().dmxSetState(id, {
        on: true,
        ...(body.subfixture !== undefined ? { subfixture: body.subfixture } : {}),
      }),
    )));
  }
  return Promise.all(
    fixtureIds.map((id) => dmxOnFixture(dmxBridgeFor(id, target, cfg), id, (conn) => conn.httpClient().dmxSetEffect(id, body))),
  );
}

/**
 * Black out the whole rig.
 *
 * Rig-wide and target-less by design - it is the lighting panic path, and a
 * kill switch that can be aimed is a kill switch that can be aimed wrong in
 * the one moment it matters. Deliberately NOT subject to `firstDmxBridge`'s
 * single-bridge rule: blackout goes to EVERY reachable bridge, for the same
 * reason `/midi/panic` does. Re-firing is half of what a panic is for, and a
 * rig-wide kill that reached one node while another kept a strobe running
 * would be the worst possible outcome.
 *
 * `[MEASURED CAVEAT]` PANIC has already been observed reaching only ONE
 * bridge on this rig (PLAN.md, CP-121 live-risk audit). This function fans
 * out deliberately; whether every bridge is reachable at the time is a
 * property of the mesh, not of this call, and the per-bridge results say
 * which answered.
 */
export async function sendDmxBlackout(target = 'all'): Promise<FireResult[]> {
  // Through the panic latch, like every PANIC leg: blackout is a safety
  // action, and "PANIC LOCKED" blocking the one control that kills the
  // lights was backwards (2026-11-03 audit A-8).
  const targets = resolveTargets(target, true);
  if (targets.length === 0) return [{ target, kind: 'no-bridges' }];
  return Promise.all(targets.map(async (cfg): Promise<FireResult> => {
    const conn = getConnection(cfg.id);
    if (!conn) return { target: cfg.id, kind: 'bridge-unknown' };
    if (conn.status() !== 'connected') {
      return { target: cfg.id, kind: 'bridge-disconnected', detail: conn.status() };
    }
    try {
      await conn.httpClient().dmxBlackout();
      // A Blackout stops every lighting layer on that bridge (DEC-601), so
      // every Light Preset button goes dark with it. Not while a share is
      // being RECORDED: that press never happened.
      if (!isCaptureBridge(cfg.id)) clearAllPresetLayers();
      return { target: cfg.id, kind: 'ok' };
    } catch (err) {
      if (err instanceof OpbridgeError) {
        return { target: cfg.id, kind: 'http-error', detail: `${err.kind}: ${err.message}` };
      }
      return { target: cfg.id, kind: 'http-error', detail: (err as Error).message };
    }
  }));
}
